import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Home, Car, Shield, Scale, HeartPulse, Plane, Store,
  IdCard, FileText, BookOpen, LineChart,
  Upload, Trash2, Plus, FileSearch, Check, X, ArrowLeft, Download, Pencil, Eye,
  ChevronDown, ChevronRight, ExternalLink, MessageCircle,
  Mail, Phone, MapPin, Calendar, Flag, BadgeCheck, Copy, Heart, User, Users2, Lock,
  QrCode, Loader2, FileSignature, ClipboardCheck, Briefcase,
  Search, UserPlus,
  Bike, Ship, Truck, Cat, Dog, Sparkles, Cpu, Camera, Gem, Anchor,
  Image as ImageIcon,
  PawPrint, Mountain, GraduationCap, Baby, Building2, Wallet,
  HeartHandshake, Activity, Cross, PiggyBank, TrendingUp,
  Bitcoin, Coins, Landmark, CandlestickChart, BadgeDollarSign, Wheat,
  KeyRound,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import CommissionsTable from "@/components/CommissionsTable";

// Lucide icon registry for custom categories (matches backend ALLOWED_CATEGORY_ICONS)
const ICON_REGISTRY = {
  Shield, Home, Car, HeartPulse, Plane, Store, Scale, Briefcase, Wallet,
  Building2, Bike, Ship, Truck, Cat, Dog, Sparkles, Cpu, Camera, Gem, Anchor,
  PawPrint, Mountain, GraduationCap, Baby,
  HeartHandshake, Activity, Cross, PiggyBank, TrendingUp,
  LineChart, Bitcoin, Coins, Landmark, CandlestickChart, BadgeDollarSign, Wheat,
};
import { TaskForm } from "@/pages/TasksPage";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import UploadPolicyDialog from "@/components/UploadPolicyDialog";
import CustomerStatusBadge from "@/components/CustomerStatusBadge";
import UploadPersonalDialog from "@/components/UploadPersonalDialog";
import PolicyStatusBadge from "@/components/PolicyStatusBadge";
import { AntraegeList, AntragDialog, ApproveAntragDialog, RejectAntragDialog } from "@/components/Antraege";
import WhatsAppReminderDialog from "@/components/WhatsAppReminderDialog";
import PolicyDetailsPreview from "@/components/PolicyDetailsPreview";
import ProvisionReadyBadge from "@/components/ProvisionReadyBadge";
import MobileQuickActions from "@/components/MobileQuickActions";
import { formatName, titleCaseName, fmtDate, whatsappLink, fmtMoney } from "@/lib/format";

function BusinessBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-md text-white shadow-sm"
      style={{
        background: "linear-gradient(135deg, #C89F50 0%, #DFB864 50%, #A07A2E 100%)",
        textShadow: "0 1px 1px rgba(0,0,0,0.15)",
      }}
      data-testid="business-badge"
    >
      <Building2 className="w-3 h-3" strokeWidth={2.2} />
      Business
    </span>
  );
}

const INSURANCE_META = {
  casa: { icon: Home }, auto: { icon: Car }, rc: { icon: Shield },
  tutela_legale: { icon: Scale }, malattia: { icon: HeartPulse },
  viaggi: { icon: Plane }, negozio: { icon: Store },
  vita_3a: { icon: PiggyBank }, vita_3b: { icon: TrendingUp },
  hypothek: { icon: Building2 }, mietkaution: { icon: KeyRound },
};
// Dokumente del Kunde (IDs)
const CLIENT_DOC_META = {
  carta_identita: { icon: IdCard },
  carta_identita_retro: { icon: IdCard },
  patente: { icon: FileText },
  permesso: { icon: BadgeCheck },
  passaporto: { icon: BookOpen },
};

/* ─────────────────────────── Anagrafica helpers ─────────────────────────── */
// Core anagrafica fields that contribute to the completeness % shown under the name.
// Order kept stable so the bar grows predictably as the consultant fills the form.
const ANAGRAFICA_CORE_FIELDS = [
  "first_name", "last_name", "email", "phone", "date_of_birth", "place_of_birth",
  "nationality", "gender", "civil_status", "address", "city", "postal_code",
  "canton", "ahv_number", "permit_type",
];

function computeAnagraficaCompleteness(client) {
  if (!client) return 0;
  const filled = ANAGRAFICA_CORE_FIELDS.filter(
    (k) => typeof client[k] === "string" ? client[k].trim() !== "" : Boolean(client[k])
  ).length;
  return Math.round((filled / ANAGRAFICA_CORE_FIELDS.length) * 100);
}

/** Age in completed years given a YYYY-MM-DD ISO date string (or null). */
function calculateAge(iso) {
  if (!iso || typeof iso !== "string") return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const [, y, mo, d] = m;
  const birth = new Date(Number(y), Number(mo) - 1, Number(d));
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const mDiff = today.getMonth() - birth.getMonth();
  if (mDiff < 0 || (mDiff === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age >= 0 && age < 150 ? age : null;
}

/** Look up a Swiss PLZ via the free OpenPLZ API → returns array of {city, canton} (deduped, can be empty). */
async function lookupSwissPLZ(plz) {
  const clean = String(plz || "").trim();
  if (!/^\d{4}$/.test(clean)) return [];
  try {
    const r = await fetch(`https://openplzapi.org/ch/Localities?postalCode=${clean}`);
    if (!r.ok) return [];
    const data = await r.json();
    if (!Array.isArray(data)) return [];
    const seen = new Set();
    const out = [];
    for (const x of data) {
      const city = x?.name || "";
      const canton = x?.canton?.shortName || "";
      const key = `${city}|${canton}`;
      if (city && !seen.has(key)) {
        seen.add(key);
        out.push({ city, canton });
      }
    }
    return out;
  } catch { return []; }
}

/** Tiny popover that lists alternative localities for a multi-locality PLZ. */
function PlzAlternativesPicker({ options = [], current, onPick, testId = "plz-alternatives" }) {
  const [open, setOpen] = useState(false);
  if (!options || options.length <= 1) return null;
  return (
    <div className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[10px] font-medium text-prime hover:underline"
        data-testid={`${testId}-toggle`}
      >
        +{options.length - 1} weitere Ort{options.length - 1 > 1 ? "e" : ""}
      </button>
      {open && (
        <div className="absolute z-10 mt-1 w-48 max-h-48 overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
          {options.map((o, i) => {
            const isCurrent = current && o.city === current.city && o.canton === current.canton;
            return (
              <button
                key={`${o.city}-${o.canton}-${i}`}
                type="button"
                onClick={() => { onPick(o); setOpen(false); }}
                className={`w-full text-left px-2 py-1.5 text-xs hover:bg-secondary ${isCurrent ? "bg-emerald-50 dark:bg-emerald-950/30 font-medium" : ""}`}
              >
                {o.city} <span className="text-muted-foreground">· {o.canton}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Slim green progress bar under client name showing anagrafica completeness. */
function AnagraficaProgress({ percent, testId = "anagrafica-progress" }) {
  const p = Math.max(0, Math.min(100, percent || 0));
  return (
    <div className="mt-2 mb-1" data-testid={testId}>
      <div className="h-1 w-full rounded-full bg-emerald-100 dark:bg-emerald-950/40 overflow-hidden">
        <div
          className="h-full bg-emerald-600 dark:bg-emerald-500 transition-all duration-500 ease-out"
          style={{ width: `${p}%` }}
          aria-valuenow={p}
          aria-valuemin={0}
          aria-valuemax={100}
          role="progressbar"
          aria-label="Vollständigkeit Stammdaten"
        />
      </div>
      <div className="flex justify-end mt-0.5">
        <span className="text-[10px] font-mono text-muted-foreground" data-testid={`${testId}-label`}>
          {p}%
        </span>
      </div>
    </div>
  );
}

/** Small family icon shown next to the client name when family members exist.
 *  Hover reveals the list of linked relatives. */
function FamilyBadge({ members = [], onClick }) {
  if (!members || members.length === 0) return null;
  const tip = members
    .map((m) => `${m.first_name || ""} ${m.last_name || ""}`.trim())
    .filter(Boolean)
    .join(" · ");
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors shrink-0"
      title={`Familienmitglieder: ${tip}`}
      data-testid="family-badge"
      aria-label={`${members.length} Familienmitglied(er)`}
    >
      <Users2 className="w-3 h-3" strokeWidth={2} />
      <span className="text-[10px] font-bold leading-none tabular-nums">{members.length}</span>
    </button>
  );
}
// Gesellschaftsdokumente (residual document types still present in the DB)
const COMPANY_DOC_META = {
  analisi_finanziaria: { icon: LineChart },
};

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [status, setStatus] = useState({});
  const [impliedBy, setImpliedBy] = useState({});
  const [categories, setCategories] = useState({ defaults: [], custom: [], allowed_icons: [] });
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [antraege, setAntraege] = useState([]);
  const [openAntrag, setOpenAntrag] = useState(false);
  const [approvingAntrag, setApprovingAntrag] = useState(null);
  const [rejectingAntrag, setRejectingAntrag] = useState(null);
  const [waTarget, setWaTarget] = useState(null);
  const [commissionPreviews, setCommissionPreviews] = useState({});
  const [documents, setDocuments] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [openPolicy, setOpenPolicy] = useState(false);
  const [editPolicy, setEditPolicy] = useState(null);
  const [expandedPolicy, setExpandedPolicy] = useState(null);
  const [openPersonal, setOpenPersonal] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [personalCollapsed, setPersonalCollapsed] = useState(true);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [familyCollapsed, setFamilyCollapsed] = useState(true);
  const [plzAlternatives, setPlzAlternatives] = useState([]);

  // Profile photo upload via file picker + QR session
  const photoFileRef = useRef(null);
  const [photoQrOpen, setPhotoQrOpen] = useState(false);
  const [photoSession, setPhotoSession] = useState(null);
  const [aufgabeOpen, setAufgabeOpen] = useState(false);
  const photoPollRef = useRef(null);

  useEffect(() => () => { if (photoPollRef.current) clearInterval(photoPollRef.current); }, []);

  const uploadProfilePhotoFile = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "personal");
      fd.append("doc_type", "profile_photo");
      await api.post(`/clients/${id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Profilfoto aggiornata");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler upload foto");
    }
  };

  const startProfilePhotoQr = async () => {
    setPhotoQrOpen(true);
    setPhotoSession(null);
    try {
      const res = await api.post("/upload-sessions", {
        purpose: "profile_photo", client_id: id,
      });
      setPhotoSession(res.data);
      photoPollRef.current = setInterval(async () => {
        try {
          const s = await api.get(`/upload-sessions/${res.data.id}`);
          if (s.data.status === "uploaded") {
            clearInterval(photoPollRef.current);
            setPhotoQrOpen(false);
            toast.success("Profilfoto vom Telefon empfangen");
            load();
          }
        } catch { /* ignore */ }
      }, 2000);
    } catch {
      toast.error("Erstellungsfehler sessione QR");
      setPhotoQrOpen(false);
    }
  };

  const stopProfilePhotoQr = () => {
    if (photoPollRef.current) clearInterval(photoPollRef.current);
    setPhotoQrOpen(false); setPhotoSession(null);
  };

  const load = async () => {
    try {
      const [c, s, p, d, cats, cp] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/clients/${id}/insurance-status`),
        api.get(`/policies`, { params: { client_id: id } }),
        api.get(`/documents/by-client/${id}`),
        api.get(`/insurance-categories`).catch(() => ({ data: { defaults: [], custom: [], allowed_icons: [] } })),
        api.get(`/policies/commission-previews`, { params: { client_id: id } }).catch(() => ({ data: {} })),
      ]);
      setClient(c.data); setDraft(c.data);
      // insurance-status now returns {status, implied_by}
      const payload = s.data || {};
      setStatus(payload.status || {});
      setImpliedBy(payload.implied_by || {});
      setPolicies(p.data); setDocuments(d.data);
      setCategories(cats.data || { defaults: [], custom: [], allowed_icons: [] });
      setCommissionPreviews(cp.data || {});
      // Load Anträge for this client
      api.get("/antraege", { params: { client_id: id } })
        .then((r) => setAntraege(r.data || []))
        .catch(() => setAntraege([]));
      // Load family members count for the header badge
      api.get(`/clients/${id}/family`)
        .then((r) => setFamilyMembers(r.data || []))
        .catch(() => setFamilyMembers([]));
    } catch {
      toast.error("Client not found");
      navigate("/clients");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Load profile photo from dedicated profile_photo document (not linked to ID)
  useEffect(() => {
    let revoke = null;
    (async () => {
      const photoDoc = documents.find((d) => d.doc_type === "profile_photo");
      if (!photoDoc) { setPhotoUrl(null); return; }
      try {
        const res = await api.get(`/documents/${photoDoc.id}/download`, { responseType: "blob" });
        const obj = URL.createObjectURL(res.data);
        setPhotoUrl(obj); revoke = obj;
      } catch { /* ignore */ }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [documents]);

  // Load ID front + back document images as blob URLs (only if they are images)
  const [idFrontUrl, setIdFrontUrl] = useState(null);
  const [idBackUrl, setIdBackUrl] = useState(null);
  useEffect(() => {
    const blobs = [];
    const isImg = (d) => (d?.content_type || "").startsWith("image/");
    (async () => {
      const front = documents.find((d) => d.doc_type === "carta_identita" && isImg(d));
      const back = documents.find((d) => d.doc_type === "carta_identita_retro" && isImg(d));
      if (front) {
        try {
          const r = await api.get(`/documents/${front.id}/download`, { responseType: "blob" });
          const u = URL.createObjectURL(r.data); blobs.push(u); setIdFrontUrl(u);
        } catch { setIdFrontUrl(null); }
      } else setIdFrontUrl(null);
      if (back) {
        try {
          const r = await api.get(`/documents/${back.id}/download`, { responseType: "blob" });
          const u = URL.createObjectURL(r.data); blobs.push(u); setIdBackUrl(u);
        } catch { setIdBackUrl(null); }
      } else setIdBackUrl(null);
    })();
    return () => blobs.forEach((u) => URL.revokeObjectURL(u));
  }, [documents]);

  const saveEdit = async () => {
    try {
      const normalized = {
        ...draft,
        first_name: draft.first_name !== undefined ? titleCaseName(draft.first_name) : draft.first_name,
        last_name: draft.last_name !== undefined ? titleCaseName(draft.last_name) : draft.last_name,
        place_of_birth: draft.place_of_birth !== undefined ? titleCaseName(draft.place_of_birth) : draft.place_of_birth,
        city: draft.city !== undefined ? titleCaseName(draft.city) : draft.city,
        nationality: draft.nationality !== undefined ? titleCaseName(draft.nationality) : draft.nationality,
        country: draft.country !== undefined ? titleCaseName(draft.country) : draft.country,
        address: draft.address !== undefined ? titleCaseName(draft.address) : draft.address,
      };
      const res = await api.patch(`/clients/${id}`, normalized);
      setClient(res.data); setEditing(false);
      toast.success("Aktualisiert");
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const deletePolicy = async (pid) => {
    if (!window.confirm(t.confirm_delete)) return;
    await api.delete(`/policies/${pid}`);
    toast.success("Gelöscht");
    load();
  };

  const deleteDoc = async (did) => {
    if (!window.confirm(t.confirm_delete)) return;
    await api.delete(`/documents/${did}`);
    toast.success("Gelöscht");
    load();
  };

  const downloadDoc = async (did, filename) => {
    const token = localStorage.getItem("crm_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/documents/${did}/download`;
    const a = document.createElement("a");
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const obj = URL.createObjectURL(blob);
    a.href = obj; a.download = filename; a.click();
    URL.revokeObjectURL(obj);
  };

  const viewDoc = async (did) => {
    try {
      const token = localStorage.getItem("crm_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/documents/${did}/download`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("not ok");
      const blob = await res.blob();
      const obj = URL.createObjectURL(blob);
      window.open(obj, "_blank", "noopener");
    } catch {
      toast.error("Vorschau nicht möglich");
    }
  };

  const openIdDocument = async () => {
    // Find latest identity-like document (carta_identita, patente, passaporto)
    const id_types = ["carta_identita", "patente"];
    const found = documents.find(d => id_types.includes(d.doc_type)) || documents[0];
    if (!found) {
      toast.error("Keine Dokumente d'identità hochgeladen per questo Kunde");
      return;
    }
    const token = localStorage.getItem("crm_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/documents/${found.id}/download`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const obj = URL.createObjectURL(blob);
    window.open(obj, "_blank", "noopener");
  };

  const openPolicyFile = async (policyId) => {
    try {
      const meta = await api.get(`/policies/${policyId}/document`);
      const doc = meta.data;
      const token = localStorage.getItem("crm_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const obj = URL.createObjectURL(blob);
      window.open(obj, "_blank", "noopener");
    } catch (err) {
      toast.error("Dokument non gefunden per questa Police");
    }
  };

  if (!client) return <div className="p-12 text-sm">Loading…</div>;

  const docsByType = {};
  documents.forEach(d => {
    if (!docsByType[d.doc_type]) docsByType[d.doc_type] = [];
    docsByType[d.doc_type].push(d);
  });

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/clients")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> {t.clients}
          </button>
          <span className="text-muted-foreground">/</span>
          <span className="text-sm">{formatName(client.first_name)} {formatName(client.last_name)}</span>
        </div>
      </div>

      <ClientTaskDialog
        open={aufgabeOpen}
        onOpenChange={setAufgabeOpen}
        clientId={client.id}
        clientName={`${client.first_name} ${client.last_name}`}
      />

      <CategoryManagerDialog
        open={catDialogOpen}
        onOpenChange={setCatDialogOpen}
        categories={categories}
        onChanged={load}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* LEFT: client card */}
        <div className="prime-card p-4 sm:p-6" data-testid="client-card">
          {idFrontUrl ? (
            // ID-card hero: front image at top, optional back thumbnail, name as subtitle
            <div>
              <div className="relative rounded-lg overflow-hidden border-2 border-prime/40 bg-card shadow-[0_4px_24px_rgba(201,164,92,0.18)] group">
                <img
                  src={idFrontUrl}
                  alt="Identitätskarte Vorderseite"
                  className="w-full h-auto object-contain cursor-zoom-in"
                  onClick={() => {
                    const front = documents.find((d) => d.doc_type === "carta_identita");
                    if (front) downloadDoc(front.id, front.filename || "id-front");
                  }}
                  data-testid="id-front-image"
                />
                {idBackUrl && (
                  <button
                    className="absolute bottom-2 right-2 w-20 h-12 rounded-md overflow-hidden border-2 border-white shadow-lg hover:scale-105 transition-transform"
                    onClick={() => {
                      const back = documents.find((d) => d.doc_type === "carta_identita_retro");
                      if (back) downloadDoc(back.id, back.filename || "id-back");
                    }}
                    title="Rückseite anzeigen"
                    data-testid="id-back-thumb"
                  >
                    <img src={idBackUrl} alt="Rückseite" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/20 flex items-end justify-center pb-0.5">
                      <span className="text-[8px] font-bold uppercase tracking-widest text-white">Rückseite</span>
                    </div>
                  </button>
                )}
              </div>
              <div className="mt-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="label-uppercase text-prime flex items-center gap-2">
                    {t.Stammdaten}
                    {client.client_type === "business" && <BusinessBadge />}
                  </div>
                  {client.client_type === "business" && client.company_name ? (
                    <>
                      <div className="font-display font-bold text-lg leading-tight truncate" data-testid="client-company-name">
                        {client.company_name}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {client.company_legal_form && <span className="font-semibold">{client.company_legal_form}</span>}
                        {client.company_uid && <span> · {client.company_uid}</span>}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        Ansprechperson: <span className="font-semibold text-foreground">{formatName(client.first_name)} {formatName(client.last_name)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="font-display font-bold text-lg leading-tight truncate" data-testid="client-name">
                        {formatName(client.first_name)} {formatName(client.last_name)}
                      </div>
                      <FamilyBadge
                        members={familyMembers}
                        onClick={() => {
                          setFamilyCollapsed(false);
                          setTimeout(() => {
                            document
                              .querySelector('[data-testid="family-panel"]')
                              ?.scrollIntoView({ behavior: "smooth", block: "center" });
                          }, 50);
                        }}
                      />
                    </div>
                  )}
                  <AnagraficaProgress percent={computeAnagraficaCompleteness(client)} testId="anagrafica-progress-id" />
                </div>
                <button
                  onClick={openIdDocument}
                  className="p-1.5 rounded text-green-600 hover:text-green-700 hover:bg-green-50 transition-colors shrink-0"
                  title="Identitätsdokument öffnen"
                  data-testid="open-id-btn"
                >
                  <IdCard className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ) : (
            // Fallback: classic avatar layout when no ID image uploaded yet
            <div className="flex items-start gap-4">
              <ProfileAvatar
                photoUrl={photoUrl}
                initials={`${(client.first_name?.[0] || "").toUpperCase()}${(client.last_name?.[0] || "").toUpperCase()}`}
                onPickQr={startProfilePhotoQr}
              />
              <input
                type="file" accept="image/*" className="hidden" ref={photoFileRef}
                id="profile-photo-gallery-input"
                onChange={(e) => { if (e.target.files?.[0]) uploadProfilePhotoFile(e.target.files[0]); e.target.value = ""; }}
                data-testid="profile-photo-file-input"
              />
              <input
                type="file" accept="image/*" capture="environment" className="hidden"
                id="profile-photo-camera-input"
                onChange={(e) => { if (e.target.files?.[0]) uploadProfilePhotoFile(e.target.files[0]); e.target.value = ""; }}
                data-testid="profile-photo-camera-input"
              />
              <div className="flex-1 min-w-0">
                <div className="label-uppercase text-prime flex items-center gap-2">
                  {t.Stammdaten}
                  {client.client_type === "business" && <BusinessBadge />}
                </div>
                {client.client_type === "business" && client.company_name ? (
                  <>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight break-words" data-testid="client-company-name-big">
                        {client.company_name}
                      </h2>
                    </div>
                    <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                      {client.company_legal_form && <span className="font-semibold">{client.company_legal_form}</span>}
                      {client.company_uid && <span className="font-mono">· {client.company_uid}</span>}
                      {client.company_start_date && <span>· seit {fmtDate(client.company_start_date)}</span>}
                    </div>
                    <div className="text-sm mt-2 inline-flex items-center gap-2">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground">Ansprechperson:</span>
                      <span className="font-semibold">{formatName(client.first_name)} {formatName(client.last_name)}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight break-words">
                      {formatName(client.first_name)} {formatName(client.last_name)}
                    </h2>
                  <FamilyBadge
                    members={familyMembers}
                    onClick={() => {
                      setFamilyCollapsed(false);
                      setTimeout(() => {
                        document
                          .querySelector('[data-testid="family-panel"]')
                          ?.scrollIntoView({ behavior: "smooth", block: "center" });
                      }, 50);
                    }}
                  />
                  {(() => {
                    const hasId = documents.some(d => ["carta_identita", "patente"].includes(d.doc_type));
                    return (
                      <button
                        onClick={openIdDocument}
                        className={`p-1.5 rounded transition-colors ${
                          hasId
                            ? "text-green-600 hover:text-green-700 hover:bg-green-50"
                            : "text-muted-foreground hover:text-prime hover:bg-secondary"
                        }`}
                        title={hasId ? "Identitätsdokument öffnen" : "Keine Dokumente hochgeladen"}
                        data-testid="open-id-btn"
                      >
                        <IdCard className="w-5 h-5" strokeWidth={1.5} />
                      </button>
                    );
                  })()}
                </div>
                )}
                <AnagraficaProgress percent={computeAnagraficaCompleteness(client)} testId="anagrafica-progress-classic" />
              </div>
            </div>
          )}

          <div className="prime-divider my-6" />

          {!editing ? (
            <div className="text-sm" data-testid="client-info">
              {/* SEZIONE Bis — Persönliche Daten (collapsible) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setPersonalCollapsed((v) => !v)}
                    className="flex items-center gap-2 label-uppercase text-muted-foreground hover:text-foreground transition-colors group"
                    data-testid="personal-data-toggle"
                    aria-expanded={!personalCollapsed}
                  >
                    {personalCollapsed
                      ? <ChevronRight className="w-3.5 h-3.5 transition-transform" strokeWidth={2} />
                      : <ChevronDown className="w-3.5 h-3.5 transition-transform" strokeWidth={2} />}
                    <BadgeCheck className="w-3 h-3" strokeWidth={2} /> {t.personal_data}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="p-1.5 rounded text-muted-foreground hover:text-prime hover:bg-secondary transition-colors"
                    title={t.edit}
                    aria-label={t.edit}
                    data-testid="edit-personal-data-btn"
                  >
                    <Pencil className="w-3.5 h-3.5" strokeWidth={1.8} />
                  </button>
                </div>
                {!personalCollapsed && (
                  <div className="rounded-md border border-border divide-y divide-border bg-card/40 animate-fade-up">
                    <InfoLine label={t.first_name} value={formatName(client.first_name)} testId="info-first-name" />
                    <InfoLine label={t.last_name} value={formatName(client.last_name)} testId="info-last-name" />
                    <InfoLine
                      label={t.date_of_birth}
                      value={(() => {
                        const d = fmtDate(client.date_of_birth);
                        const a = calculateAge(client.date_of_birth);
                        return d && d !== "—" && a != null ? `${d} · ${a} J.` : d;
                      })()}
                      testId="info-dob"
                    />
                    <InfoLine label={t.gender} value={genderLabel(client.gender, t)} testId="info-gender" />
                    <InfoLine label={t.civil_status} value={civilStatusLabel(client.civil_status, client.civil_status_date, t)} testId="info-civil-status" />
                    <InfoLine label={t.religion} value={religionLabel(client.religion, t)} testId="info-religion" />
                    <InfoLine label={t.nationality} value={client.nationality} testId="info-nationality" />
                    <InfoLine label={t.permit_type} value={permitLabel(client.permit_type)} testId="info-permit" />
                    <InfoLine label="Sprache" value={languageLabel(client.preferred_language)} testId="info-language" />
                    <InfoLine label="AHV-Nr." value={client.ahv_number} testId="info-ahv" copyable />
                    <InfoLine label="Arbeitgeber" value={client.employer} testId="info-employer" />
                    <InfoLine label="Quelle" value={client.quelle} testId="info-quelle" />
                    <InfoLine
                      label="Erstkontakt durch"
                      value={client.erstkontakt_durch === "berater" ? "Berater" : client.erstkontakt_durch === "kunde" ? "Kunde" : client.erstkontakt_durch}
                      testId="info-erstkontakt"
                    />
                  </div>
                )}
              </div>

              {/* SEZIONE B — Kontakt */}
              <div className="space-y-2 mt-4">
                <div className="label-uppercase text-muted-foreground flex items-center gap-2">
                  <Phone className="w-3 h-3" strokeWidth={2} /> {t.contacts}
                </div>
                <div className="rounded-md border border-border divide-y divide-border bg-card/40">
                  <InfoLine
                    icon={Mail}
                    label={t.email}
                    value={client.email}
                    testId="info-email"
                    copyable
                  />
                  <InfoLine
                    icon={Phone}
                    label={t.phone}
                    value={client.phone}
                    testId="info-phone"
                    copyable
                    action={client.phone && {
                      href: whatsappLink(client.phone, `Guten Tag ${client.first_name || ""}, da Wertia Finanz...`),
                      label: "WhatsApp",
                      color: "text-green-600 hover:bg-green-50",
                      external: true,
                      icon: MessageCircle,
                      testId: "whatsapp-btn",
                    }}
                  />
                  <InfoLine
                    icon={MapPin}
                    label={t.street_no}
                    value={client.address}
                    testId="info-street"
                  />
                  <InfoLine
                    icon={Building2}
                    label={t.city}
                    value={[
                      [client.postal_code, client.city].filter(Boolean).join(" "),
                      client.canton,
                    ].filter(Boolean).join(" · ")}
                    testId="info-city"
                  />
                  <InfoLine label="Wohnsituation" value={housingLabel(client.housing_situation)} testId="info-housing" />
                </div>
              </div>

              {client.notes && (
                <div className="pt-2 mt-3 border-t border-border text-xs text-muted-foreground italic">
                  {client.notes}
                </div>
              )}
              <FamilyPanel
                client={client}
                onChanged={load}
                collapsed={familyCollapsed}
                setCollapsed={setFamilyCollapsed}
              />
              <PrivateNotesPanel client={client} onSaved={(updated) => setClient(updated)} />
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              {["first_name","last_name","email","phone","date_of_birth","place_of_birth","nationality","document_number","permit_type","postal_code","address","city","canton"].map(k => {
                const isDob = k === "date_of_birth";
                const isPlz = k === "postal_code";
                const age = isDob ? calculateAge(draft[k]) : null;
                const handleChange = async (e) => {
                  const v = e.target.value;
                  setDraft({ ...draft, [k]: v });
                  if (isPlz && /^\d{4}$/.test(v.trim())) {
                    const results = await lookupSwissPLZ(v);
                    if (results.length > 0) {
                      const first = results[0];
                      setDraft((d) => ({ ...d, postal_code: v, city: first.city || d.city, canton: first.canton || d.canton }));
                      setPlzAlternatives(results);
                    } else {
                      setPlzAlternatives([]);
                    }
                  } else if (isPlz) {
                    setPlzAlternatives([]);
                  }
                };
                return (
                  <div key={k}>
                    <Label className="label-uppercase text-muted-foreground">{t[k] || k}</Label>
                    <div className="relative">
                      <Input
                        value={draft[k] || ""} onChange={handleChange}
                        className={`mt-1 h-9 ${isDob && age != null ? "pr-14" : ""}`}
                        data-testid={`edit-${k}`}
                      />
                      {isDob && age != null && (
                        <span
                          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 font-display font-bold text-base text-prime tabular-nums"
                          data-testid="dob-age-display"
                          title="Alter in Jahren"
                        >
                          {age}
                        </span>
                      )}
                    </div>
                    {isPlz && plzAlternatives.length > 1 && (
                      <div className="mt-1">
                        <PlzAlternativesPicker
                          options={plzAlternatives}
                          current={{ city: draft.city, canton: draft.canton }}
                          onPick={(o) => setDraft((d) => ({ ...d, city: o.city, canton: o.canton }))}
                          testId="edit-plz-alternatives"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
              <div>
                <Label className="label-uppercase text-muted-foreground">Zivilstand</Label>
                <Select
                  value={draft.civil_status || ""}
                  onValueChange={(v) => setDraft({
                    ...draft, civil_status: v,
                    ...(v === "celibe" ? { civil_status_date: "" } : {}),
                  })}
                >
                  <SelectTrigger className="mt-1 h-9" data-testid="edit-civil-status">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="celibe">Ledig</SelectItem>
                    <SelectItem value="sposato">Verheiratet</SelectItem>
                    <SelectItem value="separato">Getrennt</SelectItem>
                    <SelectItem value="divorziato">Geschieden</SelectItem>
                    <SelectItem value="vedovo">Verwitwet</SelectItem>
                    <SelectItem value="orfano">Waise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {draft.civil_status && draft.civil_status !== "celibe" && (
                <div>
                  <Label className="label-uppercase text-muted-foreground">
                    {draft.civil_status === "sposato" ? "Hochzeitsdatum"
                      : draft.civil_status === "separato" ? "Trennungsdatum"
                        : draft.civil_status === "divorziato" ? "Scheidungsdatum"
                          : draft.civil_status === "vedovo" ? "Datum Verwitwung"
                            : "Ereignisdatum"}
                  </Label>
                  <Input
                    value={draft.civil_status_date || ""}
                    onChange={(e) => setDraft({ ...draft, civil_status_date: e.target.value })}
                    placeholder="GG/MM/AAAA"
                    className="mt-1 h-9" data-testid="edit-civil-status-date"
                  />
                </div>
              )}
              <div>
                <Label className="label-uppercase text-muted-foreground">Geschlecht</Label>
                <Select
                  value={draft.gender || ""}
                  onValueChange={(v) => setDraft({ ...draft, gender: v })}
                >
                  <SelectTrigger className="mt-1 h-9" data-testid="edit-gender">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Männlich</SelectItem>
                    <SelectItem value="F">Weiblich</SelectItem>
                    <SelectItem value="X">Andere / Nicht spezifiziert</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-uppercase text-muted-foreground">Religion</Label>
                <Select
                  value={draft.religion || ""}
                  onValueChange={(v) => setDraft({ ...draft, religion: v })}
                >
                  <SelectTrigger className="mt-1 h-9" data-testid="edit-religion">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cattolica">Katholisch</SelectItem>
                    <SelectItem value="protestante">Protestantisch / Evangelica</SelectItem>
                    <SelectItem value="ortodossa">Orthodox</SelectItem>
                    <SelectItem value="ebraica">Jüdisch</SelectItem>
                    <SelectItem value="musulmana">Muslimisch</SelectItem>
                    <SelectItem value="buddista">Buddhistisch</SelectItem>
                    <SelectItem value="induista">Hinduistisch</SelectItem>
                    <SelectItem value="atea">Konfessionslos / Keina</SelectItem>
                    <SelectItem value="altro">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-uppercase text-muted-foreground">Sprache</Label>
                <Select
                  value={draft.preferred_language || ""}
                  onValueChange={(v) => setDraft({ ...draft, preferred_language: v })}
                >
                  <SelectTrigger className="mt-1 h-9" data-testid="edit-language">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="de">Deutsch</SelectItem>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="it">Italiano</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-uppercase text-muted-foreground">Wohnsituation</Label>
                <Select
                  value={draft.housing_situation || ""}
                  onValueChange={(v) => setDraft({ ...draft, housing_situation: v })}
                >
                  <SelectTrigger className="mt-1 h-9" data-testid="edit-housing">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mieter">Mieter</SelectItem>
                    <SelectItem value="eigentuemer">Eigentümer</SelectItem>
                    <SelectItem value="bei_eltern">Bei Eltern</SelectItem>
                    <SelectItem value="wg">WG</SelectItem>
                    <SelectItem value="altro">Andere</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-uppercase text-muted-foreground">AHV-Nr.</Label>
                <Input
                  value={draft.ahv_number || ""} onChange={e => setDraft({ ...draft, ahv_number: e.target.value })}
                  className="mt-1 h-9" placeholder="756.xxxx.xxxx.xx"
                  data-testid="edit-ahv-number"
                />
              </div>
              <div>
                <Label className="label-uppercase text-muted-foreground">Arbeitgeber</Label>
                <Input
                  value={draft.employer || ""} onChange={e => setDraft({ ...draft, employer: e.target.value })}
                  className="mt-1 h-9"
                  data-testid="edit-employer"
                />
              </div>
              <div>
                <Label className="label-uppercase text-muted-foreground">Quelle</Label>
                <Select value={draft.quelle || ""} onValueChange={(v) => setDraft({ ...draft, quelle: v })}>
                  <SelectTrigger className="mt-1 h-9" data-testid="edit-quelle">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    {["Mitarbeiterempfehlung", "Kundenempfehlung", "Socialmedia Kampagne", "Messe", "Wettbewerb", "Mundpropaganda", "Umfrage", "Steuern", "Bestandskunde", "Bestandskunde gleicher Haushalt", "Eigenkreis", "Inbound-Call"].map(q => (
                      <SelectItem key={q} value={q}>{q}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-uppercase text-muted-foreground">Erstkontakt ausgeführt durch</Label>
                <Select value={draft.erstkontakt_durch || ""} onValueChange={(v) => setDraft({ ...draft, erstkontakt_durch: v })}>
                  <SelectTrigger className="mt-1 h-9" data-testid="edit-erstkontakt">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="berater">Berater</SelectItem>
                    <SelectItem value="kunde">Kunde</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 mt-3">
                <Button onClick={saveEdit} className="btn-prime flex-1" data-testid="save-edit-btn">{t.save}</Button>
                <Button variant="ghost" onClick={() => { setEditing(false); setDraft(client); }}>{t.cancel}</Button>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: insurance matrix + tabs */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          {/* Insurance Matrix */}
          <div className="prime-card p-3 sm:p-4" data-testid="insurance-matrix">
            <div className="flex items-center justify-between mb-3 gap-3">
              <div className="min-w-0">
                <div className="label-uppercase text-muted-foreground">{t.insurance_status}</div>
                <h3 className="font-display font-bold text-base sm:text-lg tracking-tight truncate">{t.insurance_coverages}</h3>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {user?.role === "master" && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-none gap-1 h-8"
                    onClick={() => setCatDialogOpen(true)}
                    data-testid="manage-categories-btn"
                    title="Gestisci categorie"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline text-xs">{t.new_category}</span>
                  </Button>
                )}
                {user?.role !== "basic" && (
                <Button
                  size="sm"
                  onClick={() => setOpenPolicy(true)}
                  className="rounded-none bg-prime hover:bg-prime-dark text-white gap-1.5 shrink-0 h-8"
                  data-testid="upload-policy-btn"
                >
                  <Upload className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline text-xs">{t.upload_policy}</span>
                  <span className="sm:hidden text-xs">Police</span>
                </Button>
                )}
              </div>
            </div>
            <div className="flex gap-1 sm:gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
              {[...(categories.defaults || []), ...(categories.custom || [])].map((cat) => {
                const key = cat.key;
                const has = status[key];
                const implied = (impliedBy[key] || []).length > 0;
                const Icon = ICON_REGISTRY[cat.icon] || Shield;
                const label = cat.is_default ? (t.insurance_types[key] || cat.label) : cat.label;
                const tooltip = implied
                  ? `${label} — ${(impliedBy[key] || []).map(i => i.reason).join(" · ")}`
                  : `${label} — ${has ? t.has_insurance : t.no_insurance}`;
                return (
                  <div
                    key={key}
                    className="group relative flex-1 min-w-[36px] sm:min-w-0 flex items-center justify-center py-1.5 transition-all hover:scale-110"
                    data-testid={`insurance-${key}`}
                    title={tooltip}
                  >
                    {implied && (
                      <span
                        className="absolute -top-0.5 -right-0.5 bg-green-600 text-white text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-full leading-none"
                        title={tooltip}
                      >
                        incl.
                      </span>
                    )}
                    <Icon
                      className={`w-6 h-6 sm:w-7 sm:h-7 transition-colors ${
                        has ? "text-green-600" : "text-foreground/60"
                      }`}
                      strokeWidth={1.5}
                    />
                    {/* Tooltip on hover */}
                    <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-foreground text-background text-[10px] font-medium px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10 shadow-lg">
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Coverage Barometer */}
            <CoverageBarometer status={status} />
          </div>

          {/* Tabs: Anträge | Policen | Dokumente del Kunde | Gesellschaftsdokumente | Provisionen */}
          <Tabs defaultValue={(new URLSearchParams(window.location.search).get("tab")) || "antraege"} className="prime-card p-0" data-testid="detail-tabs">
            {(() => {
              const tabCls = "relative rounded-md bg-transparent shadow-none aria-[selected=true]:bg-emerald-500 aria-[selected=true]:text-white aria-[selected=true]:shadow-md aria-[selected=true]:scale-[1.02] aria-[selected=false]:text-muted-foreground hover:text-foreground h-9 sm:h-10 px-3 sm:px-4 my-1.5 mx-0.5 text-xs sm:text-sm font-semibold transition-all gap-1.5";
              const countCls = "ml-1 sm:ml-1.5 font-mono text-[10px] sm:text-xs opacity-60 group-aria-[selected=true]:opacity-100";
              const clientDocsCount = Object.keys(CLIENT_DOC_META).reduce((n, k) => n + (docsByType[k] || []).length, 0);
              const companyDocsCount = Object.keys(COMPANY_DOC_META).reduce((n, k) => n + (docsByType[k] || []).length, 0);
              const IconTab = ({ value, testId, label, Icon, count }) => (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value={value} className={tabCls} data-testid={testId} aria-label={label}>
                      <Icon className="w-4 h-4 sm:w-[18px] sm:h-[18px]" strokeWidth={1.7} />
                      <span className={countCls}>{count}</span>
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-foreground text-background text-xs font-medium">
                    {label}
                  </TooltipContent>
                </Tooltip>
              );
              return (
            <TooltipProvider delayDuration={150}>
              <TabsList className="rounded-none w-full justify-start border-b border-border bg-transparent p-0 h-11 sm:h-12 gap-0">
                <IconTab value="antraege" testId="tab-antraege" label="Anträge" Icon={FileSignature} count={antraege.length} />
                <IconTab value="policies" testId="tab-policies" label={t.tab_policies} Icon={FileText} count={policies.length} />
                <IconTab value="client_docs" testId="tab-client-docs" label={t.tab_client_docs} Icon={User} count={clientDocsCount} />
                <IconTab value="company_docs" testId="tab-company-docs" label={t.tab_company_docs} Icon={Building2} count={companyDocsCount} />
                <IconTab value="commissions" testId="tab-commissions" label={t.tab_commissions} Icon={Wallet} count={policies.length} />
              </TabsList>
            </TooltipProvider>
              );
            })()}

            <TabsContent value="policies" className="m-0 p-0">
              {policies.length === 0 ? (
                <div className="p-12 text-center text-sm text-muted-foreground">—</div>
              ) : (
                <div className="divide-y divide-border">
                  {policies.map(p => {
                    const Icon = INSURANCE_META[p.insurance_type]?.icon || Shield;
                    const d = Math.ceil((new Date(p.end_date) - new Date()) / 86400000);
                    const expired = d < 0;
                    const critical = d <= 30 && !expired;
                    const isHealth = p.insurance_type === "malattia";
                    const expanded = expandedPolicy === p.id;
                    return (
                      <div key={p.id} data-testid={`policy-row-${p.id}`}>
                        <div className={`p-3 sm:p-4 flex items-start gap-3 sm:gap-4 hover:bg-secondary group ${expired ? "bg-red-50 dark:bg-red-950/20" : ""}`}>
                          <div className={`w-10 h-10 flex items-center justify-center rounded-md border shrink-0 transition-colors ${
                            expired
                              ? "border-red-500/60 bg-red-50 dark:bg-red-950/30"
                              : critical
                              ? "border-prime/40 bg-card"
                              : "border-green-500/60 bg-card"
                          }`}>
                            <Icon
                              className={`w-5 h-5 ${expired ? "text-red-600" : critical ? "text-prime" : "text-green-600"}`}
                              strokeWidth={1.5}
                            />
                          </div>
                          <button onClick={() => setExpandedPolicy(expanded ? null : p.id)} className="flex-1 min-w-0 text-left space-y-1" data-testid={`expand-policy-${p.id}`} title="Espandi Details">
                            <div className="flex items-baseline gap-2 sm:gap-3 flex-wrap">
                              {expanded
                                ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground inline" />
                                : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground inline" />}
                              <span className="font-semibold text-sm hover:text-prime truncate max-w-[140px] sm:max-w-none">{p.company}</span>
                              <span className="text-xs font-mono text-muted-foreground">{p.policy_number}</span>
                              <PolicyStatusBadge status={p.status || "bozza"} />
                              {expired && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-red-600 text-white" data-testid={`policy-expired-badge-${p.id}`}>
                                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                                  Abgelaufen
                                </span>
                              )}
                              {!isHealth && (
                                <span className="text-xs text-muted-foreground hidden sm:inline">— {t.insurance_types[p.insurance_type]}</span>
                              )}
                              <CustomerStatusBadge status={p.customer_status} />
                              <ProvisionReadyBadge insuranceType={p.insurance_type} premium={p.premium} details={p.details} testId={p.id} />
                              {p.whatsapp_last_sent_at && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 dark:text-emerald-400" title={`Von ${p.whatsapp_last_sent_by_name || "—"} · Sprache: ${(p.whatsapp_last_sent_lang || "de").toUpperCase()} · ${p.whatsapp_send_count}× gesendet`}>
                                  <MessageCircle className="w-3 h-3" strokeWidth={2} />
                                  WA: {(() => {
                                    const d = Math.floor((new Date() - new Date(p.whatsapp_last_sent_at)) / 86400000);
                                    if (d === 0) return "heute";
                                    if (d === 1) return "gestern";
                                    return `vor ${d}T`;
                                  })()}
                                </span>
                              )}
                            </div>
                            {isHealth ? (
                              <PolicyDetailsPreview details={p.details || {}} type="malattia" t={t} />
                            ) : (
                              <>
                                <div className="text-xs font-mono">
                                  <span className="text-green-700 font-semibold">{fmtDate(p.start_date)}</span>
                                  <span className="text-muted-foreground"> → </span>
                                  <span className="text-red-700 font-semibold">{fmtDate(p.end_date)}</span>
                                </div>
                                {p.details && Object.keys(p.details).length > 0 && (
                                  <PolicyDetailsPreview details={p.details} type={p.insurance_type} t={t} />
                                )}
                              </>
                            )}
                          </button>
                          <div className="text-right shrink-0">
                            <div className={`text-xs font-mono ${expired ? "text-red-600 font-bold" : critical ? "text-prime font-bold" : ""}`}>
                              {expired ? `−${Math.abs(d)}d` : `${d}d`}
                            </div>
                            {p.premium > 0 && <div className="label-uppercase text-muted-foreground text-[10px] sm:text-xs whitespace-nowrap">{p.currency} {fmtMoney(p.premium)}</div>}
                            {(() => {
                              const cp = commissionPreviews[p.id];
                              if (!cp) return null;
                              // Show commission for ALL users on their own policies.
                              // Hide only when there's truly nothing to show (no pct AND no gross amount).
                              if (!cp.pct && !cp.gross) return null;
                              // Basic users see only the CHF amount they will receive — never the %.
                              const isBasic = user?.role === "basic";
                              const showPct = !isBasic && cp.pct;
                              return (
                                <div
                                  className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-[10px] font-bold whitespace-nowrap"
                                  title={showPct ? `${cp.pct}% di ${cp.currency} ${fmtMoney(cp.premium)}` : `${cp.currency} ${fmtMoney(cp.premium)}`}
                                  data-testid={`commission-preview-${p.id}`}
                                >
                                  <Wallet className="w-3 h-3" strokeWidth={2} />
                                  {cp.currency} {fmtMoney(cp.gross)}{showPct ? ` · ${cp.pct}%` : ""}
                                </div>
                              );
                            })()}
                          </div>
                          <div className="flex items-center shrink-0">
                            {(expired || critical) && (
                              <Button
                                variant="ghost" size="icon"
                                className="h-8 w-8 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                                onClick={(e) => { e.stopPropagation(); setWaTarget({ client: client, policy: p }); }}
                                data-testid={`wa-policy-${p.id}`}
                                title="WhatsApp-Erinnerung senden"
                              >
                                <MessageCircle className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); openPolicyFile(p.id); }} data-testid={`open-policy-${p.id}`} title="Policen-Datei öffnen">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 hidden sm:inline-flex" onClick={(e) => { e.stopPropagation(); setEditPolicy(p); }} data-testid={`edit-policy-${p.id}`} title="Bearbeiten">
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 hidden sm:inline-flex" onClick={(e) => { e.stopPropagation(); deletePolicy(p.id); }} data-testid={`delete-policy-${p.id}`} title="Löschen">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                        {expanded && (
                          <div className="px-3 sm:px-4 pb-3 sm:pb-0 space-y-3">
                            <div className="flex gap-2 sm:hidden mb-2">
                              <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => setEditPolicy(p)} data-testid={`edit-policy-m-${p.id}`}>
                                <Pencil className="w-3.5 h-3.5" /> Bearbeiten
                              </Button>
                              <Button variant="outline" size="sm" className="flex-1 gap-1 text-destructive" onClick={() => deletePolicy(p.id)} data-testid={`delete-policy-m-${p.id}`}>
                                <Trash2 className="w-3.5 h-3.5" /> Löschen
                              </Button>
                            </div>
                            <PolicyWorkflowActions
                              policy={p}
                              userRole={user?.role}
                              onChanged={(updated) => {
                                setPolicies(policies.map(x => x.id === updated.id ? updated : x));
                              }}
                            />
                            <PolicyExpandedNotes policy={p} t={t} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="antraege" className="m-0 p-0">
              <AntraegeList
                items={antraege}
                clientId={id}
                role={user?.role}
                autoExpandId={new URLSearchParams(window.location.search).get("antrag") || null}
                onAdd={() => setOpenAntrag(true)}
                onApprove={(a) => setApprovingAntrag(a)}
                onReject={(a) => setRejectingAntrag(a)}
                onUpdate={(updated) => {
                  setAntraege((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
                }}
                onDelete={async (a) => {
                  if (!window.confirm("Antrag löschen?")) return;
                  try {
                    await api.delete(`/antraege/${a.id}`);
                    setAntraege((prev) => prev.filter((x) => x.id !== a.id));
                    toast.success("Antrag gelöscht");
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Fehler");
                  }
                }}
              />
            </TabsContent>

            <TabsContent value="client_docs" className="m-0 p-0">
              <DocsList
                meta={CLIENT_DOC_META} docsByType={docsByType}
                t={t} setOpenPersonal={setOpenPersonal}
                downloadDoc={downloadDoc} deleteDoc={deleteDoc} viewDoc={viewDoc}
                groupKey="client" onRenamed={load}
              />
            </TabsContent>

            <TabsContent value="company_docs" className="m-0 p-0">
              <DocsList
                meta={COMPANY_DOC_META} docsByType={docsByType}
                t={t} setOpenPersonal={setOpenPersonal}
                downloadDoc={downloadDoc} deleteDoc={deleteDoc} viewDoc={viewDoc}
                groupKey="company" onRenamed={load}
              />
            </TabsContent>

            <TabsContent value="commissions" className="m-0 p-0">
              <CommissionsTable
                policies={policies}
                commissionPreviews={commissionPreviews}
                userRole={user?.role}
                userFinma={user?.finma_register_nr}
                onRefresh={async (policyId) => {
                  try {
                    const tId = toast.loading("Dokument wird neu gelesen…", { duration: 30000 });
                    const res = await api.post(`/policies/${policyId}/refresh-commission`);
                    const fresh = await api.get(`/policies/commission-previews`, { params: { client_id: id } });
                    setCommissionPreviews(fresh.data || {});
                    toast.dismiss(tId);
                    if (res.data?.refreshed) {
                      toast.success(res.data?.message || "Provision aktualisiert");
                      if (res.data?.ai_message) toast.warning(res.data.ai_message);
                    } else {
                      toast.warning(res.data?.message || "Keine Berechnungsdaten vorhanden");
                    }
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Aktualisierung fehlgeschlagen");
                  }
                }}
                onChangeStatus={async (policyId, status) => {
                  try {
                    await api.post(`/policies/${policyId}/commission-payment`, { status });
                    setPolicies((prev) => prev.map((x) => x.id === policyId ? {
                      ...x,
                      commission_status: status,
                      commission_status_at: new Date().toISOString(),
                      commission_paid: status === "paid",
                      commission_paid_at: status === "paid" ? new Date().toISOString() : null,
                    } : x));
                    const label = status === "paid" ? "bezahlt" : status === "carried_over" ? "auf nächsten Monat verschoben" : status === "processing" ? "in Bearbeitung" : "unbezahlt";
                    toast.success(`Provision markiert als ${label}`);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Fehler");
                  }
                }}
              />
            </TabsContent>

          </Tabs>
        </div>
      </div>

      {/* Aktionen-Werkzeugleiste — sotto il blocco Policen */}
      <div className="prime-card p-4 sm:p-5" data-testid="client-actions-toolbar">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-prime" strokeWidth={1.7} />
            <span className="label-uppercase text-prime">Dokumente · Mandate · Analyse</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            className="gap-2 rounded-none border-amber-600/60 text-amber-700 hover:bg-amber-50 hover:text-amber-700 font-semibold"
            onClick={() => navigate(`/clients/${client.id}/dossier`)}
            data-testid="open-dossier-btn"
          >
            <LineChart className="w-4 h-4" /> Finanzanalyse
          </Button>
          <Button
            variant="outline"
            className="gap-2 rounded-none border-prime/40 text-prime hover:bg-prime/5 hover:text-prime"
            onClick={() => navigate(`/compliance?client=${client.id}`)}
            data-testid="open-compliance-btn"
          >
            <Sparkles className="w-4 h-4" /> AI Compliance
          </Button>
          <Button
            variant="outline"
            className="gap-2 rounded-none"
            onClick={() => setAufgabeOpen(true)}
            data-testid="open-aufgabe-btn"
          >
            <ClipboardCheck className="w-4 h-4" /> Aufgabe
          </Button>
          <div className="flex-1" />
        </div>
      </div>

      <UploadPolicyDialog
        open={openPolicy} onOpenChange={setOpenPolicy}
        clientId={id} onCreated={load}
        clientFirstName={client?.first_name || ""}
        clientLastName={client?.last_name || ""}
      />
      <UploadPolicyDialog
        open={!!editPolicy} onOpenChange={(v) => !v && setEditPolicy(null)}
        clientId={id} policy={editPolicy} onCreated={load}
        clientFirstName={client?.first_name || ""}
        clientLastName={client?.last_name || ""}
      />
      <UploadPersonalDialog
        open={!!openPersonal} onOpenChange={(v) => !v && setOpenPersonal(null)}
        clientId={id} docType={openPersonal} onCreated={load}
      />

      <AntragDialog
        open={openAntrag}
        onOpenChange={setOpenAntrag}
        clientId={id}
        clientFirstName={client?.first_name || ""}
        clientLastName={client?.last_name || ""}
        onCreated={(a) => setAntraege((prev) => [a, ...prev])}
      />
      <ApproveAntragDialog
        antrag={approvingAntrag}
        onOpenChange={() => setApprovingAntrag(null)}
        onApproved={(updated) => {
          setAntraege((prev) => prev.map((x) => x.id === updated.id ? updated : x));
          load();
        }}
      />
      <RejectAntragDialog
        antrag={rejectingAntrag}
        onOpenChange={() => setRejectingAntrag(null)}
        onRejected={(updated) => {
          setAntraege((prev) => prev.map((x) => x.id === updated.id ? updated : x));
          load();
        }}
      />

      <WhatsAppReminderDialog
        open={!!waTarget}
        onOpenChange={() => setWaTarget(null)}
        client={waTarget?.client}
        policy={waTarget?.policy}
        advisorName={user?.full_name}
        onSent={load}
      />

      {/* Profile photo QR dialog */}
      <Dialog open={photoQrOpen} onOpenChange={(v) => { if (!v) stopProfilePhotoQr(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="label-uppercase text-prime flex items-center gap-1.5">
              <QrCode className="w-3 h-3" /> Profilfoto da telefono
            </div>
            <DialogTitle>Inquadra il QR e scatta la foto</DialogTitle>
          </DialogHeader>
          <div className="text-center">
            {photoSession ? (
              <>
                <div className="mx-auto bg-white p-3 rounded-lg inline-block border border-border">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=8&data=${encodeURIComponent(`${window.location.origin}/m/u/${photoSession.id}`)}`}
                    alt="QR" className="w-[280px] h-[280px] block" data-testid="profile-qr-image"
                  />
                </div>
                <div className="mt-4 flex items-center justify-center gap-2 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin text-prime" />
                  <span className="text-muted-foreground">Warten auf Foto vom Telefon…</span>
                </div>
                <ol className="text-xs text-muted-foreground mt-3 text-left max-w-xs mx-auto space-y-1">
                  <li>1. Öffnen la fotocamera sul telefono del Kunde</li>
                  <li>2. Inquadra il QR — si aprirà una pagina web</li>
                  <li>3. Il Kunde sceglie una foto o ne scatta una nuova</li>
                  <li>4. La foto profilo si aggiorna automaticamente</li>
                </ol>
              </>
            ) : (
              <div className="py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" /></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={stopProfilePhotoQr}>Abbrechen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile-only floating action button — scanner, signature, tandem, e-mail */}
      <MobileQuickActions client={client} onAfterUpload={load} />
    </div>
  );
}

function ProfileAvatar({ photoUrl, initials, onPickQr }) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="relative shrink-0" data-testid="client-photo-wrap">
      <button
        type="button"
        onClick={() => setMenuOpen((o) => !o)}
        className="group w-16 h-16 bg-foreground text-background flex items-center justify-center font-display font-bold text-2xl rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-prime transition-all relative"
        title={photoUrl ? "Profilfoto ändern" : "Profilfoto hochladen"}
        data-testid="client-photo"
      >
        {photoUrl ? (
          <img src={photoUrl} alt="profilo" className="w-full h-full object-cover" />
        ) : (
          <>{initials}</>
        )}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Upload className="w-5 h-5 text-white" strokeWidth={2} />
        </div>
      </button>

      {menuOpen && (
        <>
          <button
            type="button" onClick={() => setMenuOpen(false)}
            className="fixed inset-0 z-40 cursor-default"
            aria-label="close"
          />
          <div className="absolute z-50 mt-2 w-56 bg-card border border-border rounded-lg shadow-lg overflow-hidden text-foreground">
            <label
              htmlFor="profile-photo-camera-input"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-left hover:bg-secondary transition-colors cursor-pointer"
              data-testid="photo-menu-camera"
            >
              <Camera className="w-4 h-4 text-foreground" strokeWidth={1.7} />
              <span>Scatta foto</span>
            </label>
            <label
              htmlFor="profile-photo-gallery-input"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-left hover:bg-secondary transition-colors border-t border-border cursor-pointer"
              data-testid="photo-menu-gallery"
            >
              <ImageIcon className="w-4 h-4 text-foreground" strokeWidth={1.7} />
              <span>Scegli dalla galleria</span>
            </label>
            <button
              onClick={() => { setMenuOpen(false); onPickQr(); }}
              className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-left hover:bg-secondary transition-colors border-t border-border"
              data-testid="photo-menu-qr"
            >
              <QrCode className="w-4 h-4 text-prime" strokeWidth={1.7} />
              <span>Manda al Kunde (QR)</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function fmtMoneySafe(v) {
  if (v == null || v === "") return "";
  const n = typeof v === "number" ? v : Number(String(v).replace(/[^\d.-]/g, ""));
  if (!Number.isFinite(n) || String(v).match(/[a-zA-Z]/)) return String(v);
  return fmtMoney(n);
}

function DocRow({ d, downloadDoc, deleteDoc, viewDoc, onRenamed }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(d.filename || "");
  const save = async () => {
    const v = value.trim();
    if (!v || v === d.filename) { setEditing(false); return; }
    try {
      await api.patch(`/documents/${d.id}/rename`, { filename: v });
      toast.success("Dokument umbenannt");
      setEditing(false);
      onRenamed?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Umbenennen");
    }
  };
  return (
    <div className="ml-12 flex items-center gap-2 text-xs py-1.5">
      <FileText className="w-3.5 h-3.5 text-green-600 shrink-0" strokeWidth={1.5} />
      {editing ? (
        <>
          <input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") { setEditing(false); setValue(d.filename || ""); }
            }}
            className="flex-1 min-w-0 bg-card border border-prime/60 rounded px-2 py-1 text-xs font-mono focus:outline-none"
            data-testid={`rename-input-${d.id}`}
          />
          <button onClick={save} className="text-green-600 shrink-0" title="Speichern" data-testid={`rename-save-${d.id}`}>
            <Check className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
          <button onClick={() => { setEditing(false); setValue(d.filename || ""); }} className="text-muted-foreground shrink-0" title="Abbrechen" data-testid={`rename-cancel-${d.id}`}>
            <X className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        </>
      ) : (
        <>
          <span className="flex-1 truncate font-mono">{d.filename}</span>
          <button onClick={() => { setValue(d.filename || ""); setEditing(true); }} className="text-muted-foreground hover:text-prime shrink-0" title="Umbenennen" data-testid={`rename-doc-${d.id}`}>
            <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
          <button onClick={() => viewDoc && viewDoc(d.id)} className="text-prime hover:underline shrink-0" title="Anzeigen" data-testid={`view-doc-${d.id}`}>
            <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
          <button onClick={() => downloadDoc(d.id, d.filename)} className="text-prime hover:underline shrink-0" title="Herunterladen" data-testid={`download-doc-${d.id}`}>
            <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
          <button onClick={() => deleteDoc(d.id)} className="text-destructive hover:underline shrink-0" title="Löschen" data-testid={`delete-doc-${d.id}`}>
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
        </>
      )}
    </div>
  );
}

function DocsList({ meta, docsByType, t, setOpenPersonal, downloadDoc, deleteDoc, viewDoc, groupKey, onRenamed }) {
  return (
    <div className="divide-y divide-border">
      {Object.entries(meta).map(([key, { icon: Icon }]) => {
        const docs = docsByType[key] || [];
        const present = docs.length > 0;
        return (
          <div key={key} className="p-4" data-testid={`${groupKey}-section-${key}`}>
            <div className="flex items-center justify-between mb-2 gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-10 h-10 flex items-center justify-center rounded-md border transition-colors shrink-0 ${
                  present ? "border-green-500/60 bg-card" : "border-border bg-card"
                }`}>
                  <Icon
                    className={`w-5 h-5 ${present ? "text-green-600" : "text-foreground/70"}`}
                    strokeWidth={1.5}
                  />
                </div>
                <div className="min-w-0">
                  <div className={`text-sm font-medium truncate ${present ? "text-green-700" : ""}`}>
                    {t.personal_types[key] || key}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {docs.length} {t.documents.toLowerCase()}
                  </div>
                </div>
              </div>
              <Button
                variant="outline" size="sm"
                className="rounded-none border-foreground gap-1 shrink-0"
                onClick={() => setOpenPersonal(key)}
                data-testid={`upload-${groupKey}-${key}`}
              >
                <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> {t.upload}
              </Button>
            </div>
            {docs.map(d => (
              <DocRow key={d.id} d={d} downloadDoc={downloadDoc} deleteDoc={deleteDoc} viewDoc={viewDoc} onRenamed={onRenamed} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function CoverageBarometer({ status }) {
  const { t } = useI18n();
  const keys = Object.keys(status || {});
  const total = keys.length;
  const covered = keys.filter((k) => status[k]).length;
  const pct = total > 0 ? Math.round((covered / total) * 100) : 0;

  // Color thresholds: red <40, amber <70, green >=70
  const labelColor = pct >= 70 ? "text-green-700 dark:text-green-400"
    : pct >= 40 ? "text-orange-600 dark:text-orange-400" : "text-red-600 dark:text-red-400";
  const status_label = pct >= 100 ? t.coverage_complete_label
    : pct >= 70 ? t.coverage_good_label
      : pct >= 40 ? t.coverage_partial_label
        : pct > 0 ? t.coverage_low_label
          : t.coverage_none_label;

  return (
    <div className="mt-3 pt-3 border-t border-border" data-testid="coverage-barometer">
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="label-uppercase text-muted-foreground">{t.coverage_barometer}</div>
        <div className="flex items-baseline gap-2">
          <span className={`font-display font-extrabold text-xl ${labelColor}`}>
            {pct}%
          </span>
          <span className="text-[10px] text-muted-foreground font-mono">
            {covered}/{total}
          </span>
        </div>
      </div>
      {/* Full-gradient termometer track always visible; faint white overlay on the unfilled side to keep gradient visible */}
      <div className="relative h-2.5 rounded-full overflow-hidden bg-gradient-to-r from-red-500 via-orange-400 via-yellow-400 to-green-500 shadow-inner ring-1 ring-black/5">
        <div
          className="absolute top-0 right-0 bottom-0 bg-background/60 backdrop-blur-[1px] transition-all duration-700 ease-out"
          style={{ width: `${Math.max(0, 100 - pct)}%` }}
        />
        {/* Threshold ticks */}
        <div className="absolute inset-y-0 left-[40%] w-px bg-foreground/30" />
        <div className="absolute inset-y-0 left-[70%] w-px bg-foreground/30" />
        {/* Position knob — always visible, snaps to current pct (or 0) */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 bg-background border-2 border-foreground rounded-full shadow-md transition-all duration-700 ease-out"
          style={{ left: `${pct}%` }}
        />
      </div>
      <div className="flex justify-between mt-1 text-[9px] font-mono uppercase tracking-wider">
        <span className={pct < 40 ? "text-red-600 font-bold" : "text-muted-foreground"}>{t.coverage_high_risk}</span>
        <span className={pct >= 40 && pct < 70 ? "text-orange-600 font-bold" : "text-muted-foreground"}>{t.coverage_partial}</span>
        <span className={pct >= 70 ? "text-green-700 font-bold" : "text-muted-foreground"}>{t.coverage_covered}</span>
      </div>
      <div className={`text-[11px] mt-1 font-medium ${labelColor}`}>{status_label}</div>
    </div>
  );
}

function civilStatusLabel(status, date, t) {
  if (!status) return "";
  const label = (t && t[`civil_status_${status}`]) || status;
  if (status === "celibe" || !date) return label;
  return `${label} · ${date}`;
}

function genderLabel(g, t) {
  if (!g) return "";
  return (t && t[`gender_${g}`]) || g;
}

function religionLabel(r, t) {
  if (!r) return "";
  return (t && t[`religion_${r}`]) || r;
}

function languageLabel(lang) {
  const map = { de: "Deutsch", fr: "Français", it: "Italiano", en: "English" };
  return map[lang] || lang || "";
}

function housingLabel(h) {
  const map = {
    mieter: "Mieter",
    eigentuemer: "Eigentümer",
    bei_eltern: "Bei Eltern",
    wg: "WG",
    altro: "Andere",
  };
  return map[h] || h || "";
}

function permitLabel(p) {
  const map = {
    B: "Ausländerausweis B",
    L: "Ausländerausweis L",
    C: "Ausländerausweis C",
    Swisspass: "Swisspass",
    "Meldebestätigung": "Meldebestätigung",
    CartaIdentita: "Carta d'identità",
    Patente: "Patente",
    Passaporto: "Passaporto",
    Other: "Other",
  };
  return map[p] || p || "";
}

function InfoLine({ label, icon: LeadingIcon, value, action, testId, copyable }) {
  const [copied, setCopied] = React.useState(false);
  const display = value || "—";
  const isEmpty = !value;

  const doCopy = (e) => {
    e.stopPropagation();
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      toast.success("Kopiert");
      setTimeout(() => setCopied(false), 1200);
    }).catch(() => {});
  };

  return (
    <div
      className={`group flex items-center gap-2 px-2.5 py-1.5 ${isEmpty ? "opacity-60" : "hover:bg-secondary/50"} transition-colors`}
      data-testid={testId}
    >
      {LeadingIcon ? (
        <span
          className="w-7 shrink-0 flex items-center justify-center"
          title={label}
          aria-label={label}
        >
          <LeadingIcon className="w-4 h-4 text-prime" strokeWidth={1.8} />
        </span>
      ) : (
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground w-[88px] shrink-0">
          {label}
        </span>
      )}
      <span className="flex-1 min-w-0 text-sm break-words leading-snug" title={value || ""}>{display}</span>
      {copyable && !isEmpty && (
        <button
          onClick={doCopy}
          className="p-1 text-muted-foreground hover:text-foreground rounded opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
          title="Kopieren"
          data-testid={`${testId}-copy`}
        >
          {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
        </button>
      )}
      {action?.href && (
        <a
          href={action.href}
          target={action.external ? "_blank" : undefined}
          rel={action.external ? "noopener noreferrer" : undefined}
          className={`p-1.5 rounded transition-colors shrink-0 ${action.color || "text-prime hover:bg-prime/10"}`}
          title={action.label}
          data-testid={action.testId}
          onClick={(e) => e.stopPropagation()}
        >
          {action.icon
            ? <action.icon className="w-3.5 h-3.5" strokeWidth={1.7} />
            : <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.7} />}
        </a>
      )}
    </div>
  );
}


function IconRow({ icon: Icon, value, action, testId, copyable }) {
  const [copied, setCopied] = React.useState(false);
  const display = value || "—";
  const isEmpty = !value;

  const doCopy = (e) => {
    e.stopPropagation();
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      toast.success("Kopiert");
      setTimeout(() => setCopied(false), 1200);
    }).catch(() => {});
  };

  return (
    <div
      className={`group flex items-center gap-3 px-2 py-0.5 rounded transition-colors ${
        isEmpty ? "opacity-50" : "hover:bg-secondary/60"
      }`}
      data-testid={testId}
    >
      <div className={`w-9 h-9 flex items-center justify-center rounded-lg shrink-0 ${
        isEmpty
          ? "bg-secondary text-muted-foreground"
          : "bg-slate-100 dark:bg-white/5 text-slate-700 dark:text-slate-200"
      }`}>
        <Icon className="w-4 h-4" strokeWidth={1.8} />
      </div>
      <span className="flex-1 min-w-0 text-sm break-words leading-snug" title={value || ""}>{display}</span>
      {copyable && !isEmpty && (
        <button
          onClick={doCopy}
          className="p-1 text-muted-foreground hover:text-foreground rounded opacity-0 group-hover:opacity-100 transition-opacity"
          title="Kopieren"
          data-testid={`${testId}-copy`}
        >
          {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
        </button>
      )}
      {action?.href && (
        <a
          href={action.href}
          target={action.external ? "_blank" : undefined}
          rel={action.external ? "noopener noreferrer" : undefined}
          className={`p-1.5 rounded transition-colors ${action.color || "text-prime hover:bg-prime/10"}`}
          title={action.label}
          data-testid={action.testId}
          onClick={(e) => e.stopPropagation()}
        >
          {action.icon
            ? <action.icon className="w-3.5 h-3.5" strokeWidth={1.7} />
            : <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.7} />}
        </a>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="label-uppercase text-muted-foreground">{label}</span>
      <span className="text-right truncate">{value || "—"}</span>
    </div>
  );
}

function PhoneRow({ label, value, clientName }) {
  const wa = whatsappLink(value, `Guten Tag ${clientName?.split(" ")[0] || ""}, da Wertia Finanz...`);
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="label-uppercase text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-right truncate">{value || "—"}</span>
        {wa && (
          <a
            href={wa} target="_blank" rel="noopener noreferrer"
            className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
            title="WhatsApp öffnen"
            data-testid="whatsapp-btn"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
          </a>
        )}
      </div>
    </div>
  );
}

// PolicyDetailsPreview component is now in /app/frontend/src/components/PolicyDetailsPreview.jsx

const INSURANCE_TYPE_LABEL_DE = {
  casa: "Casa / Hausrat", auto: "Auto / Motorfahrzeug", rc: "Responsabilità Civile / Haftpflicht",
  tutela_legale: "Tutela legale / Rechtsschutz", malattia: "Malattia / Krankenversicherung",
  viaggi: "Viaggi / Reise", negozio: "Esercizio / Geschäft",
  vita_3a: "Vita 3A / Säule 3A gebundene Vorsorge",
  vita_3b: "Vita 3B / Säule 3B freie Vorsorge",
  hypothek: "Ipoteca / Hypothek",
  mietkaution: "Cauzione affitto / Mietkaution",
};

// Use shared formatter
const fmtDateLocal = fmtDate;

function PolicyWorkflowActions({ policy, userRole, onChanged }) {
  const [busy, setBusy] = React.useState(false);
  const role = ({ admin: "master", consultant: "basic" })[userRole] || userRole || "basic";
  const status = policy.status || "bozza";

  const COLOR_FILL = {
    amber: "bg-amber-600 hover:bg-amber-700 text-white",
    sky: "bg-sky-600 hover:bg-sky-700 text-white",
    emerald: "bg-emerald-600 hover:bg-emerald-700 text-white",
    indigo: "bg-indigo-600 hover:bg-indigo-700 text-white",
    green: "bg-green-600 hover:bg-green-700 text-white",
    violet: "bg-violet-600 hover:bg-violet-700 text-white",
    red: "bg-red-600 hover:bg-red-700 text-white",
    slate: "bg-slate-600 hover:bg-slate-700 text-white",
  };
  const COLOR_OUTLINE = {
    amber: "border-amber-300 text-amber-700 hover:bg-amber-50",
    sky: "border-sky-300 text-sky-700 hover:bg-sky-50",
    emerald: "border-emerald-300 text-emerald-700 hover:bg-emerald-50",
    indigo: "border-indigo-300 text-indigo-700 hover:bg-indigo-50",
    green: "border-green-300 text-green-700 hover:bg-green-50",
    violet: "border-violet-300 text-violet-700 hover:bg-violet-50",
    red: "border-red-300 text-red-700 hover:bg-red-50",
    slate: "border-slate-300 text-slate-700 hover:bg-slate-50",
  };

  const transition = async (next, note = "") => {
    setBusy(true);
    try {
      const res = await api.patch(`/policies/${policy.id}/status`, { status: next, note });
      toast.success("Status aggiornato");
      onChanged?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setBusy(false); }
  };

  // Decide which actions to show
  const actions = [];
  if (role === "basic") {
    if (status === "bozza") actions.push({ label: "In Revision senden", next: "in_Revision", color: "amber" });
    if (status === "in_Revision") actions.push({ label: "Dokumente komplett", next: "documenti_completi", color: "sky" });
    if (status === "documenti_completi") actions.push({ label: "Entwurf wieder öffnen", next: "in_Revision", color: "slate", outline: true });
  }
  if (role === "control" || role === "master") {
    if (status === "in_Revision") {
      actions.push({ label: "Dokumente OK", next: "documenti_completi", color: "sky" });
      actions.push({ label: "Zurück zum Entwurf", next: "bozza", color: "slate", outline: true });
    }
    if (status === "documenti_completi") {
      actions.push({ label: "Genehmigen", next: "genehmigtto_control", color: "emerald" });
      actions.push({ label: "Zurückweisen", next: "in_Revision", color: "amber", outline: true });
    }
    if (status === "genehmigtto_control") {
      actions.push({ label: "→ Warten auf Gesellschaft", next: "attesa_compagnia", color: "indigo" });
    }
  }
  if (role === "master") {
    if (status === "attesa_compagnia") actions.push({ label: "Aktiv", next: "attivo", color: "green" });
    // Master shortcuts — sempre disponibili in qualsiasi stato (tranne quello terminale dell'azione stessa)
    if (status !== "attivo") {
      actions.push({ label: "Als aktiv markieren", next: "attivo", color: "green", outline: true });
    }
    if (status !== "pagato") {
      actions.push({ label: "Als bezahlt markieren", next: "pagato", color: "violet" });
    }
    if (status !== "stornato" && status !== "bozza")
      actions.push({ label: "Stornieren", next: "stornato", color: "red", outline: true });
  }

  const history = policy.status_history || [];

  return (
    <div className="bg-secondary/40 border border-border rounded p-3" data-testid={`policy-workflow-${policy.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="label-uppercase text-muted-foreground">Status Dossier</span>
          <PolicyStatusBadge status={status} />
          {history.length > 0 && (
            <span className="text-[10px] text-muted-foreground">
              · ultimo aggiornamento {fmtDateLocal(history[history.length - 1].at)} da {history[history.length - 1].by_user_name || "—"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {actions.map((a) => (
            <Button
              key={a.next}
              size="sm"
              variant={a.outline ? "outline" : "default"}
              className={`h-8 ${a.outline ? COLOR_OUTLINE[a.color] : COLOR_FILL[a.color]}`}
              onClick={() => transition(a.next)}
              disabled={busy}
              data-testid={`transition-${a.next}-${policy.id}`}
            >
              {a.label}
            </Button>
          ))}
          {actions.length === 0 && (
            <span className="text-xs text-muted-foreground">Keina azione disponibile per il tuo ruolo.</span>
          )}
        </div>
      </div>
      {history.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border space-y-1">
          {history.slice(-3).reverse().map((h, idx) => (
            <div key={idx} className="text-[11px] flex items-center gap-2 text-muted-foreground">
              <PolicyStatusBadge status={h.status} className="!py-0" />
              <span>{fmtDateLocal(h.at)}</span>
              <span>· {h.by_user_name || "—"}</span>
              {h.note && <span className="italic">— "{h.note}"</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PolicyExpandedNotes({ policy, t }) {  const p = policy;
  const d = p.details || {};
  const type = p.insurance_type;
  const days = Math.ceil((new Date(p.end_date) - new Date(p.start_date)) / 86400000);
  const lines = [];

  lines.push(["Typ assicurazione", INSURANCE_TYPE_LABEL_DE[type] || t.insurance_types[type] || type]);
  lines.push(["Gesellschaft", p.company]);
  lines.push(["N° Police", p.policy_number]);
  lines.push(["Inizio", fmtDateLocal(p.start_date), "green"]);
  lines.push(["Fine", fmtDateLocal(p.end_date), "red"]);
  lines.push(["Gesamtdauer", `${days} Tage`]);
  if (p.premium > 0) lines.push(["Jahresprämie", `${p.currency} ${fmtMoney(p.premium)}`]);

  // Type-specific
  if (type === "malattia") {
    if (d.kvg_start || d.kvg_end) {
      lines.push(["KVG inizio", fmtDateLocal(d.kvg_start), "green"]);
      lines.push(["KVG fine", fmtDateLocal(d.kvg_end), "red"]);
    }
    if (d.vvg_start || d.vvg_end) {
      lines.push(["VVG inizio", fmtDateLocal(d.vvg_start), "green"]);
      lines.push(["VVG fine", fmtDateLocal(d.vvg_end), "red"]);
    }
    if (d.medical_model) lines.push(["Modello medico", d.medical_model]);
  } else if (type === "auto") {
    if (d.vehicle?.make) lines.push(["Marca", d.vehicle.make]);
    if (d.vehicle?.model) lines.push(["Modello", d.vehicle.model]);
    if (d.vehicle?.plate) lines.push(["Targa / Kontrollschild", d.vehicle.plate]);
    if (d.vehicle?.vin) lines.push(["VIN", d.vehicle.vin]);
    if (d.deductible) lines.push(["Franchigia", d.deductible]);
  } else if (type === "casa") {
    if (d.tenure) lines.push(["Typ", d.tenure === "affitto" ? "Affitto" : "Privata (proprietà)"]);
    if (d.address) lines.push(["Adresse assicurato", d.address]);
  } else if (type === "rc") {
    if (d.scope) lines.push(["Ambito", d.scope === "familiare" ? "Familienmitglied" : "Personale"]);
  } else if (type === "viaggi") {
    if (d.geographic_scope) lines.push(["Area geografica", d.geographic_scope]);
  } else if (type === "negozio") {
    if (d.business_name) lines.push(["Vorname esercizio", d.business_name]);
  }

  if (p.notes) lines.push(["Notizen", p.notes]);

  return (
    <div className="bg-secondary/40 border-t border-border px-6 py-5" data-testid={`expanded-policy-${p.id}`}>
      <div className="label-uppercase text-prime mb-3">Zusammenfassung Police</div>

      {/* Main fields - text under text */}
      <div className="space-y-1.5 text-sm font-mono">
        {lines.map(([label, value, color], i) => (
          <div key={i} className="flex gap-3">
            <span className="font-bold text-black min-w-[180px]">{label}:</span>
            <span className={
              color === "green" ? "text-green-700 font-semibold" :
              color === "red" ? "text-red-700 font-semibold" :
              "text-foreground"
            }>{value || "—"}</span>
          </div>
        ))}
      </div>

      {/* Coverages list */}
      {Array.isArray(d.coverages) && d.coverages.length > 0 && (
        <div className="mt-5">
          <div className="font-bold text-black text-sm mb-2">Abdeckungen incluse ({d.coverages.length}):</div>
          <div className="space-y-1.5 text-sm font-mono pl-4 border-l-2 border-prime/30">
            {d.coverages.map((c, i) => (
              <div key={i} className="flex flex-wrap gap-x-3">
                <span className="text-foreground">• {c.name || "—"}</span>
                {(c.value || c.sum_insured) && (
                  <span className="text-muted-foreground">somma: <span className="text-foreground">{fmtMoneySafe(c.value || c.sum_insured)}</span></span>
                )}
                {c.premium > 0 && (
                  <span className="text-muted-foreground">premio: <span className="text-foreground">{fmtMoney(c.premium)}</span></span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Family members for malattia */}
      {type === "malattia" && Array.isArray(d.family_members) && d.family_members.length > 0 && (
        <div className="mt-5">
          <div className="font-bold text-black text-sm mb-2">Familiari assicurati ({d.family_members.length}):</div>
          <div className="space-y-3 text-sm font-mono pl-4 border-l-2 border-prime/30">
            {d.family_members.map((m, i) => (
              <div key={i} className="space-y-0.5">
                <div className="font-semibold text-foreground">{(m.first_name || "") + " " + (m.last_name || "")}</div>
                {(m.kvg_start || m.kvg_end) && (
                  <div className="pl-3">
                    <span className="font-bold text-black">KVG:</span>{" "}
                    <span className="text-green-700 font-semibold">{m.kvg_start || "—"}</span>
                    <span className="text-muted-foreground"> → </span>
                    <span className="text-red-700 font-semibold">{m.kvg_end || "—"}</span>
                  </div>
                )}
                {(m.vvg_start || m.vvg_end) && (
                  <div className="pl-3">
                    <span className="font-bold text-black">VVG:</span>{" "}
                    <span className="text-green-700 font-semibold">{m.vvg_start || "—"}</span>
                    <span className="text-muted-foreground"> → </span>
                    <span className="text-red-700 font-semibold">{m.vvg_end || "—"}</span>
                  </div>
                )}
                {m.doctor && <div className="pl-3 text-muted-foreground">Medico: <span className="text-foreground">{m.doctor}</span></div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {(!d || Object.keys(d).length === 0) && type !== "malattia" && (
        <div className="mt-3 text-xs text-muted-foreground italic">
          Kein Detailso aggiuntivo. Clicca sulla matita per aggiungere coperture e specifiche.
        </div>
      )}
    </div>
  );
}



function ClientTaskDialog({ open, onOpenChange, clientId, clientName }) {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [draft, setDraft] = useState({
    title: "", description: "", due_date: "", priority: "medium",
    assigned_to: user?.id || "", client_id: clientId,
  });

  useEffect(() => {
    if (!open) return;
    setDraft((d) => ({ ...d, client_id: clientId, assigned_to: d.assigned_to || user?.id }));
    api.get("/users/team").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  }, [open, clientId, user]);

  const save = async () => {
    if (!draft.title.trim()) {
      toast.error("Eingeben un titolo");
      return;
    }
    try {
      const payload = { ...draft, client_id: clientId };
      if (!payload.due_date) delete payload.due_date;
      await api.post("/tasks", payload);
      toast.success("Aufgabe erstellt");
      onOpenChange(false);
      setDraft({ title: "", description: "", due_date: "", priority: "medium", assigned_to: user?.id || "", client_id: clientId });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="client-task-dialog">
        <DialogHeader>
          <DialogTitle>Neue Aufgabe · {clientName}</DialogTitle>
        </DialogHeader>
        <TaskForm draft={draft} setDraft={setDraft} users={users} clients={[]} lockedClientId={clientId} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button className="btn-prime" onClick={save} data-testid="client-task-save">Anlegen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CustomCategoryRow({ cat, onRemove, onSaved }) {
  const Ic = ICON_REGISTRY[cat.icon] || Shield;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(cat.label);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(cat.label); }, [cat.label]);

  const save = async () => {
    const newLabel = (draft || "").trim();
    if (!newLabel || newLabel === cat.label) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.patch(`/insurance-categories/${cat.id}`, { label: newLabel });
      toast.success("Kategorie umbenannt");
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-3 px-3 py-2" data-testid={`category-row-${cat.key}`}>
      <Ic className="w-4 h-4 text-foreground/70" strokeWidth={1.7} />
      {editing ? (
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setDraft(cat.label); setEditing(false); } }}
          autoFocus
          className="h-7 text-sm flex-1"
          data-testid={`edit-category-input-${cat.key}`}
        />
      ) : (
        <div className="flex-1 text-sm">{cat.label}</div>
      )}
      <span className="text-[10px] font-mono text-muted-foreground">{cat.key}</span>
      {editing ? (
        <>
          <button
            onClick={save}
            disabled={saving}
            className="text-green-600 hover:text-green-700 disabled:opacity-50"
            data-testid={`save-category-${cat.key}`}
            title="Speichern"
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            onClick={() => { setDraft(cat.label); setEditing(false); }}
            className="text-muted-foreground hover:text-foreground"
            data-testid={`cancel-edit-category-${cat.key}`}
            title="Abbrechen"
          >
            <X className="w-4 h-4" />
          </button>
        </>
      ) : (
        <>
          <button
            onClick={() => setEditing(true)}
            className="text-muted-foreground hover:text-foreground"
            data-testid={`edit-category-${cat.key}`}
            title="Umbenennen"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={() => onRemove(cat)}
            className="text-muted-foreground hover:text-prime"
            data-testid={`delete-category-${cat.key}`}
            title="Löschen"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </>
      )}
    </div>
  );
}


function CategoryManagerDialog({ open, onOpenChange, categories, onChanged }) {
  const allowed = categories?.allowed_icons || [];
  const custom = categories?.custom || [];
  const [label, setLabel] = useState("");
  const [icon, setIcon] = useState("Shield");
  const [creating, setCreating] = useState(false);
  const [orphans, setOrphans] = useState({ types: [], orphan_count: 0 });
  const [orphansLoading, setOrphansLoading] = useState(false);

  // Load coverage diagnostic when the dialog opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setOrphansLoading(true);
    api.get("/insurance-categories/orphan-types")
      .then((r) => { if (!cancelled) setOrphans(r.data || { types: [], orphan_count: 0 }); })
      .catch(() => { if (!cancelled) setOrphans({ types: [], orphan_count: 0 }); })
      .finally(() => { if (!cancelled) setOrphansLoading(false); });
    return () => { cancelled = true; };
  }, [open, categories]);

  const create = async () => {
    if (!label.trim()) {
      toast.error("Eingeben un nome");
      return;
    }
    setCreating(true);
    try {
      await api.post("/insurance-categories", { key: label, label: label.trim(), icon });
      toast.success("Kategorie erstellt");
      setLabel(""); setIcon("Shield");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setCreating(false);
    }
  };

  // Pre-fill the create form from an orphan row (Master will then click "Anlegen").
  const adoptOrphan = (row) => {
    setLabel(row.label || row.insurance_type);
    if (row.suggested_icon && allowed.includes(row.suggested_icon)) {
      setIcon(row.suggested_icon);
    }
    // Smooth-scroll to the create section if possible
    setTimeout(() => {
      document.querySelector('[data-testid="new-category-label"]')?.focus();
    }, 50);
  };

  const remove = async (cat) => {
    if (!window.confirm(`Löschen la categoria "${cat.label}"?`)) return;
    try {
      await api.delete(`/insurance-categories/${cat.id}`);
      toast.success("Kategorie gelöscht");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="category-manager-dialog">
        <DialogHeader>
          <DialogTitle>Categorie assicurative personalizzate</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Coverage diagnostic widget — shows which insurance_type values
              found in policies do not yet have an icon configured. */}
          <div className="border border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-950/15 rounded-md p-3" data-testid="orphan-types-widget">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div>
                <div className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wider flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" />
                  Policen-Typen aus dem DB
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {orphans.orphan_count > 0
                    ? `${orphans.orphan_count} Typ(en) ohne Icon — Versicherungsbarometer ignoriert sie`
                    : "Alle Policen-Typen sind durch ein Icon abgedeckt"}
                </div>
              </div>
            </div>
            {orphansLoading ? (
              <div className="text-[11px] text-muted-foreground italic">Lade…</div>
            ) : orphans.types.length === 0 ? (
              <div className="text-[11px] text-muted-foreground italic">Keine Policen im System</div>
            ) : (
              <div className="space-y-1">
                {orphans.types.map((row) => {
                  const Ic = ICON_REGISTRY[row.suggested_icon] || Shield;
                  const statusColor = row.has_icon
                    ? "bg-green-600 text-white"
                    : row.implicit_via
                      ? "bg-sky-600 text-white"
                      : "bg-red-600 text-white";
                  const statusLabel = row.has_icon
                    ? "Icon aktiv"
                    : row.implicit_via
                      ? `via ${row.implicit_via}`
                      : "ohne Icon";
                  return (
                    <div
                      key={row.insurance_type}
                      className="flex items-center gap-2 px-2 py-1.5 bg-card border border-border rounded text-[11px]"
                      data-testid={`orphan-row-${row.insurance_type}`}
                    >
                      <Ic className="w-3.5 h-3.5 text-foreground/70 shrink-0" strokeWidth={1.7} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{row.label}</div>
                        <div className="font-mono text-[9px] text-muted-foreground truncate">{row.insurance_type} · {row.count} Police(n)</div>
                      </div>
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded leading-none ${statusColor}`}>
                        {statusLabel}
                      </span>
                      {!row.has_icon && !row.implicit_via && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 rounded-none text-[10px] gap-1"
                          onClick={() => adoptOrphan(row)}
                          data-testid={`adopt-orphan-${row.insurance_type}`}
                        >
                          <Plus className="w-3 h-3" /> Icon anlegen
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Existing custom categories */}
          {custom.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs">Categorie aggiuntive</Label>
              <div className="border border-border rounded divide-y divide-border max-h-44 overflow-y-auto">
                {custom.map((c) => (
                  <CustomCategoryRow
                    key={c.id}
                    cat={c}
                    onRemove={remove}
                    onSaved={onChanged}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Create new */}
          <div className="space-y-2">
            <Label className="text-xs">Neue Kategorie</Label>
            <Input
              placeholder="Es. Animali domestici, Cyber, Drone, Cavallo…"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              data-testid="new-category-label"
            />
            <div>
              <div className="text-xs text-muted-foreground mb-1.5">Icona</div>
              <div className="grid grid-cols-8 gap-1.5 max-h-48 overflow-y-auto p-1 border border-border rounded">
                {allowed.map((name) => {
                  const Ic = ICON_REGISTRY[name] || Shield;
                  const active = icon === name;
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setIcon(name)}
                      className={`w-9 h-9 flex items-center justify-center rounded transition-colors ${
                        active ? "bg-prime text-white" : "hover:bg-secondary text-foreground/70"
                      }`}
                      data-testid={`icon-${name}`}
                      title={name}
                    >
                      <Ic className="w-4 h-4" strokeWidth={1.7} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Schliessen</Button>
          <Button className="btn-prime" onClick={create} disabled={creating} data-testid="create-category-btn">
            <Plus className="w-4 h-4 mr-1" /> Anlegen categoria
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PrivateNotesPanel({ client, onSaved }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(client.private_notes || "");
  const [saving, setSaving] = useState(false);
  const dirty = (value || "") !== (client.private_notes || "");

  // sync when client changes from outside
  React.useEffect(() => {
    setValue(client.private_notes || "");
  }, [client.id, client.private_notes]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`/clients/${client.id}`, { private_notes: value });
      toast.success("Private Notizen gespeichert");
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setSaving(false);
    }
  };

  const hasNotes = (client.private_notes || "").trim().length > 0;

  return (
    <div className="mt-4 border-t border-border pt-3" data-testid="private-notes-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-1 text-left"
        data-testid="toggle-private-notes"
      >
        <div className="flex items-center gap-2">
          <Lock className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.7} />
          <span className="label-uppercase text-muted-foreground">{t.private_notes}</span>
          {hasNotes && (
            <span className="w-1.5 h-1.5 rounded-full bg-prime" title="Notizen vorhanden" />
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-2 space-y-2">
          <Textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={4}
            placeholder="Jahrtazioni interne visibili solo al consulente — preferenze, situazione familiare, contesto, follow-up…"
            className="text-sm resize-none bg-secondary/40"
            data-testid="private-notes-input"
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] text-muted-foreground flex items-center gap-1">
              <Lock className="w-3 h-3" /> Nicht in PDF / Freigaben enthalten
            </span>
            <Button
              size="sm"
              className="btn-prime h-7 px-3 text-xs"
              onClick={save}
              disabled={!dirty || saving}
              data-testid="save-private-notes"
            >
              {saving ? "Speichern…" : "Speichern"}
            </Button>
          </div>
        </div>
      )}

      {!open && hasNotes && (
        <div className="mt-2 text-xs text-muted-foreground italic line-clamp-2 pl-1" data-testid="private-notes-preview">
          {client.private_notes}
        </div>
      )}
    </div>
  );
}


const REL_META = {
  coniuge:  { label: "Ehepartner",   color: "bg-pink-100 text-pink-700 dark:bg-pink-500/15 dark:text-pink-300" },
  figlio:   { label: "Sohn",    color: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300" },
  figlia:   { label: "Tochter",    color: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300" },
  padre:    { label: "Vater",     color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" },
  madre:    { label: "Mutter",     color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" },
  fratello: { label: "Bruder",  color: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300" },
  sorella:  { label: "Schwester",   color: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300" },
  genitore: { label: "Elternteil",  color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" },
  figlio_o_figlia: { label: "Sohn/a", color: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300" },
  fratello_o_sorella: { label: "Bruder/Schwester", color: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300" },
};

function FamilyPanel({ client, onChanged, collapsed: collapsedProp, setCollapsed: setCollapsedProp }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [allClients, setAllClients] = useState([]);
  const [search, setSearch] = useState("");
  const [relationship, setRelationship] = useState("coniuge");
  const [localCollapsed, setLocalCollapsed] = useState(true);
  const collapsed = collapsedProp !== undefined ? collapsedProp : localCollapsed;
  const setCollapsed = setCollapsedProp || setLocalCollapsed;

  const load = async () => {
    try {
      const r = await api.get(`/clients/${client.id}/family`);
      setMembers(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [client.id]);

  useEffect(() => {
    if (!dialogOpen) return;
    api.get("/clients").then((r) => setAllClients(r.data)).catch(() => setAllClients([]));
    setSearch(""); setRelationship("coniuge");
  }, [dialogOpen]);

  const linkedIds = new Set([client.id, ...members.map(m => m.client_id)]);
  const candidates = allClients
    .filter((c) => !linkedIds.has(c.id))
    .filter((c) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return `${c.first_name} ${c.last_name}`.toLowerCase().includes(q)
          || (c.email || "").toLowerCase().includes(q);
    })
    .slice(0, 20);

  const addLink = async (targetId) => {
    try {
      await api.post(`/clients/${client.id}/family`, {
        target_client_id: targetId, relationship,
      });
      toast.success("Familienmitglied verbunden");
      await load();
      onChanged?.();
      setDialogOpen(false);
      // — Smart cross-link: if we just linked a child, and the main client has a spouse,
      //   offer to also link the child as a child of the spouse (in one click).
      if (relationship === "figlio" || relationship === "figlia") {
        const spouse = members.find((m) => m.relationship === "coniuge");
        if (spouse && spouse.client_id !== targetId) {
          // Re-fetch the new child to get its gender (drives figlio vs figlia)
          let childRel = relationship;
          try {
            const c = await api.get(`/clients/${targetId}`);
            if (c.data?.gender === "F") childRel = "figlia";
            else if (c.data?.gender === "M") childRel = "figlio";
          } catch { /* keep relationship as default */ }
          toast(
            `Auch mit Ehepartner ${spouse.first_name || ""} ${spouse.last_name || ""} verbinden?`,
            {
              duration: 10000,
              action: {
                label: "Verbinden",
                onClick: async () => {
                  try {
                    await api.post(`/clients/${spouse.client_id}/family`, {
                      target_client_id: targetId,
                      relationship: childRel,
                    });
                    toast.success(`Auch mit ${spouse.first_name} verbunden`);
                    await load();
                    onChanged?.();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Verknüpfung fehlgeschlagen");
                  }
                },
              },
            }
          );
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const removeLink = async (targetId, name) => {
    if (!window.confirm(`Verbindung entfernen mit ${name}?`)) return;
    try {
      await api.delete(`/clients/${client.id}/family/${targetId}`);
      toast.success("Verbindung entfernt");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div className="mt-4 border-t border-border pt-3" data-testid="family-panel">
      <div className="flex items-center justify-between mb-2 px-1">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="flex items-center gap-2 hover:text-foreground transition-colors"
          data-testid="family-toggle"
          aria-expanded={!collapsed}
        >
          {collapsed
            ? <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={2} />
            : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={2} />}
          <Users2 className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.7} />
          <span className="label-uppercase text-muted-foreground">{t.family}</span>
          {members.length > 0 && (
            <span className="text-[10px] font-mono text-muted-foreground">{members.length}</span>
          )}
        </button>
        <button
          type="button"
          onClick={() => { setCollapsed(false); setDialogOpen(true); }}
          className="text-prime hover:underline text-[11px] font-medium flex items-center gap-1"
          data-testid="add-family-btn"
        >
          <Plus className="w-3 h-3" /> {t.add}
        </button>
      </div>

      {!collapsed && (
        members.length === 0 ? (
          <div className="text-xs text-muted-foreground italic px-1 pb-1 animate-fade-up">Keine verbundenen Familienmitglieder</div>
        ) : (
          <div className="space-y-1 animate-fade-up">
            {members.map((m) => {
              const meta = REL_META[m.relationship] || { label: m.relationship, color: "bg-secondary text-foreground" };
              const relLabel = t[`relationship_${m.relationship}`] || meta.label;
              return (
                <div
                  key={m.client_id}
                  className="group flex items-center gap-2 px-2 py-1.5 rounded hover:bg-secondary/60 transition-colors"
                  data-testid={`family-member-${m.client_id}`}
                >
                  <button
                    type="button"
                    onClick={() => navigate(`/clients/${m.client_id}`)}
                    className="flex items-center gap-2 flex-1 min-w-0 text-left"
                  >
                    <div className="w-7 h-7 rounded-full bg-foreground/80 text-background flex items-center justify-center text-[11px] font-medium shrink-0">
                      {(m.first_name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate hover:text-prime transition-colors">
                        {m.first_name} {m.last_name}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${meta.color}`}>
                          {relLabel}
                        </span>
                        {m.date_of_birth && (
                          <span className="text-[10px] text-muted-foreground font-mono">{m.date_of_birth}</span>
                        )}
                      </div>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                  <button
                    onClick={() => removeLink(m.client_id, `${m.first_name} ${m.last_name}`)}
                    className="text-muted-foreground hover:text-prime opacity-0 group-hover:opacity-100 transition-opacity p-1"
                    data-testid={`remove-family-${m.client_id}`}
                    title="Entfernen"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="family-dialog">
          <DialogHeader>
            <DialogTitle>Familienmitglied hinzufügen</DialogTitle>
            <DialogDescription>
              Verbinden Sie ein bestehendes Mitglied oder legen Sie ein neues an — manuell oder per KI-Scan.
            </DialogDescription>
          </DialogHeader>

          {/* Beziehung — sempre visibile, condivisa tra tutti i tab */}
          <div>
            <Label className="text-xs">Beziehung</Label>
            <Select value={relationship} onValueChange={setRelationship}>
              <SelectTrigger className="h-9" data-testid="family-relationship-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="coniuge">{t.relationship_coniuge} ({t.relationship_marito} / {t.relationship_moglie})</SelectItem>
                <SelectItem value="figlio">{t.relationship_figlio}</SelectItem>
                <SelectItem value="figlia">{t.relationship_figlia}</SelectItem>
                <SelectItem value="padre">{t.relationship_padre}</SelectItem>
                <SelectItem value="madre">{t.relationship_madre}</SelectItem>
                <SelectItem value="fratello">{t.relationship_fratello}</SelectItem>
                <SelectItem value="sorella">{t.relationship_sorella}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Tabs defaultValue="search" className="mt-2">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="search" data-testid="family-tab-search" className="gap-1.5">
                <Search className="w-3.5 h-3.5" /> Suchen
              </TabsTrigger>
              <TabsTrigger value="manual" data-testid="family-tab-manual" className="gap-1.5">
                <UserPlus className="w-3.5 h-3.5" /> Manuell
              </TabsTrigger>
              <TabsTrigger value="ai" data-testid="family-tab-ai" className="gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> Mit KI
              </TabsTrigger>
            </TabsList>

            <TabsContent value="search" className="space-y-3 mt-3">
              <div>
                <Label className="text-xs">Kunde suchen</Label>
                <Input
                  placeholder="Vorname, Nachname oder E-Mail…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="family-search-input"
                />
              </div>
              <div className="max-h-64 overflow-y-auto border border-border rounded divide-y divide-border">
                {candidates.length === 0 ? (
                  <div className="p-4 text-center text-xs text-muted-foreground">
                    {search ? "Keine Kunden gefunden" : "Beginnen Sie zu tippen, um einen Kunden zu finden"}
                  </div>
                ) : (
                  candidates.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => addLink(c.id)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-secondary/60 text-left"
                      data-testid={`family-candidate-${c.id}`}
                    >
                      <div className="w-7 h-7 rounded-full bg-foreground/80 text-background flex items-center justify-center text-[11px] font-medium shrink-0">
                        {(c.first_name || "?").slice(0, 1).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{c.first_name} {c.last_name}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{c.email || "—"}</div>
                      </div>
                      <Plus className="w-4 h-4 text-prime" />
                    </button>
                  ))
                )}
              </div>
            </TabsContent>

            <TabsContent value="manual" className="mt-3">
              <QuickAddFamilyForm
                key={`manual-${dialogOpen}`}
                ownerClientId={client.id}
                ownerClient={client}
                relationship={relationship}
                onCreated={async (newClientId) => {
                  await addLink(newClientId);
                }}
                onClose={() => setDialogOpen(false)}
              />
            </TabsContent>

            <TabsContent value="ai" className="mt-3">
              <AiAddFamilyForm
                key={`ai-${dialogOpen}`}
                ownerClientId={client.id}
                ownerClient={client}
                relationship={relationship}
                onCreated={async (newClientId) => {
                  await addLink(newClientId);
                }}
                onClose={() => setDialogOpen(false)}
              />
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Schliessen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Quick manual form: creates a new client + links it as family member of ownerClientId.
 *  Minimum: Vorname, Nachname, Geburtsdatum. Address is pre-filled from the parent client. */
function QuickAddFamilyForm({ ownerClientId, ownerClient, relationship, onCreated, onClose }) {
  const hasOwnerAddress = !!(ownerClient?.address || ownerClient?.postal_code || ownerClient?.city);
  const [sameHousehold, setSameHousehold] = useState(hasOwnerAddress);
  const [form, setForm] = useState({
    first_name: "", last_name: "", date_of_birth: "",
    gender: "", phone: "", email: "",
    // Inherit address from the parent client (family members usually live together)
    address: ownerClient?.address || "",
    city: ownerClient?.city || "",
    postal_code: ownerClient?.postal_code || "",
    canton: ownerClient?.canton || "",
  });
  const [busy, setBusy] = useState(false);
  const [plzAlternatives, setPlzAlternatives] = useState([]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleSameHousehold = () => {
    setSameHousehold((on) => {
      const nv = !on;
      if (nv) {
        // Re-copy owner's address
        setForm((f) => ({
          ...f,
          address: ownerClient?.address || "",
          city: ownerClient?.city || "",
          postal_code: ownerClient?.postal_code || "",
          canton: ownerClient?.canton || "",
        }));
        setPlzAlternatives([]);
      } else {
        // Clear so user can type a different address
        setForm((f) => ({ ...f, address: "", city: "", postal_code: "", canton: "" }));
      }
      return nv;
    });
  };

  const handlePlzChange = async (v) => {
    setForm((f) => ({ ...f, postal_code: v }));
    if (/^\d{4}$/.test(v.trim())) {
      const results = await lookupSwissPLZ(v);
      if (results.length > 0) {
        const first = results[0];
        setForm((f) => ({ ...f, postal_code: v, city: first.city || f.city, canton: first.canton || f.canton }));
        setPlzAlternatives(results);
      } else {
        setPlzAlternatives([]);
      }
    } else {
      setPlzAlternatives([]);
    }
  };

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("Vorname und Nachname sind erforderlich");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/clients", {
        ...form,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        client_type: "person",
        // Required by backend — sensible defaults for family members of an existing client
        quelle: "Bestandskunde gleicher Haushalt",
        erstkontakt_durch: "berater",
      });
      const newId = res.data?.id;
      if (!newId) throw new Error("Keine Client-ID zurückgegeben");
      await onCreated(newId);
      toast.success(`${form.first_name} ${form.last_name} angelegt und verbunden`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="family-quick-form">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Vorname *</Label>
          <Input value={form.first_name} onChange={(e) => update("first_name", e.target.value)} className="h-9" data-testid="quick-first-name" />
        </div>
        <div>
          <Label className="text-xs">Nachname *</Label>
          <Input value={form.last_name} onChange={(e) => update("last_name", e.target.value)} className="h-9" data-testid="quick-last-name" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Geburtsdatum</Label>
          <Input type="date" value={form.date_of_birth} onChange={(e) => update("date_of_birth", e.target.value)} className="h-9" data-testid="quick-dob" />
        </div>
        <div>
          <Label className="text-xs">Geschlecht</Label>
          <Select value={form.gender} onValueChange={(v) => update("gender", v)}>
            <SelectTrigger className="h-9" data-testid="quick-gender"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="M">Männlich</SelectItem>
              <SelectItem value="F">Weiblich</SelectItem>
              <SelectItem value="X">Andere</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Telefon</Label>
          <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} className="h-9" data-testid="quick-phone" />
        </div>
        <div>
          <Label className="text-xs">E-Mail</Label>
          <Input value={form.email} onChange={(e) => update("email", e.target.value)} className="h-9" data-testid="quick-email" />
        </div>
      </div>
      {hasOwnerAddress && (
        <label className="flex items-center gap-2 cursor-pointer select-none py-1" data-testid="quick-same-household">
          <input
            type="checkbox"
            checked={sameHousehold}
            onChange={toggleSameHousehold}
            className="w-4 h-4 accent-prime"
          />
          <span className="text-xs text-foreground">
            Gleiches Domizil wie Hauptkunde
            <span className="ml-1 text-muted-foreground">
              ({[ownerClient.postal_code, ownerClient.city].filter(Boolean).join(" ")})
            </span>
          </span>
        </label>
      )}
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs">PLZ</Label>
          <Input
            value={form.postal_code}
            onChange={(e) => handlePlzChange(e.target.value)}
            disabled={sameHousehold && hasOwnerAddress}
            className="h-9 disabled:opacity-60"
            data-testid="quick-postal-code"
          />
          {plzAlternatives.length > 1 && (
            <div className="mt-1">
              <PlzAlternativesPicker
                options={plzAlternatives}
                current={{ city: form.city, canton: form.canton }}
                onPick={(o) => setForm((f) => ({ ...f, city: o.city, canton: o.canton }))}
                testId="quick-plz-alternatives"
              />
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs">Ort</Label>
          <Input value={form.city} onChange={(e) => update("city", e.target.value)} disabled={sameHousehold && hasOwnerAddress} className="h-9 disabled:opacity-60" data-testid="quick-city" />
        </div>
        <div>
          <Label className="text-xs">Kanton</Label>
          <Input value={form.canton} onChange={(e) => update("canton", e.target.value)} disabled={sameHousehold && hasOwnerAddress} className="h-9 disabled:opacity-60" data-testid="quick-canton" />
        </div>
      </div>
      <div>
        <Label className="text-xs flex items-center gap-1">
          Adresse
          {ownerClient?.address && sameHousehold && (
            <span className="text-[10px] text-emerald-700 dark:text-emerald-400 font-normal">· aus Hauptkunde übernommen</span>
          )}
        </Label>
        <Input value={form.address} onChange={(e) => update("address", e.target.value)} disabled={sameHousehold && hasOwnerAddress} className="h-9 disabled:opacity-60" data-testid="quick-address" />
      </div>
      <Button onClick={submit} disabled={busy} className="w-full" data-testid="quick-submit">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
        Anlegen & verbinden
      </Button>
      <div className="text-[11px] text-muted-foreground text-center">
        Das neue Mitglied wird als eigenständiger Kunde erstellt und sofort verknüpft (Beziehung: {relationship}).
      </div>
    </div>
  );
}

/** KI-Upload form: upload ID/passport → AI extraction → review & confirm → create + link.
 *  Address fields fall back to the parent client's address if KI didn't extract them. */
function AiAddFamilyForm({ ownerClientId, ownerClient, relationship, onCreated, onClose }) {
  const fileRef = useRef(null);
  const hasOwnerAddress = !!(ownerClient?.address || ownerClient?.postal_code || ownerClient?.city);
  const [sameHousehold, setSameHousehold] = useState(hasOwnerAddress);
  const [docId, setDocId] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [form, setForm] = useState({
    first_name: "", last_name: "", date_of_birth: "",
    place_of_birth: "", nationality: "", document_number: "",
    permit_type: "",
    // Pre-fill from parent client — usually family lives together
    address: ownerClient?.address || "",
    city: ownerClient?.city || "",
    postal_code: ownerClient?.postal_code || "",
    canton: ownerClient?.canton || "",
  });

  const toggleSameHousehold = () => {
    setSameHousehold((on) => {
      const nv = !on;
      if (nv) {
        setForm((f) => ({
          ...f,
          address: ownerClient?.address || "",
          city: ownerClient?.city || "",
          postal_code: ownerClient?.postal_code || "",
          canton: ownerClient?.canton || "",
        }));
      } else {
        setForm((f) => ({ ...f, address: "", city: "", postal_code: "", canton: "" }));
      }
      return nv;
    });
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExtracting(true);
    try {
      // 1. Anonymous upload
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/documents/upload-anonymous", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const did = up.data?.id;
      setDocId(did);
      // 2. Extract
      const ex = await api.post("/ai/extract-identity", { document_id: did, provider: "claude" });
      setExtracted(ex.data);
      setForm((f) => ({
        ...f,
        first_name: ex.data.first_name || f.first_name,
        last_name: ex.data.last_name || f.last_name,
        date_of_birth: ex.data.date_of_birth || f.date_of_birth,
        place_of_birth: ex.data.place_of_birth || f.place_of_birth,
        nationality: ex.data.nationality || f.nationality,
        document_number: ex.data.document_number || f.document_number,
        permit_type: ex.data.permit_type || f.permit_type,
        // Only override inherited address if KI extracted something explicit
        address: ex.data.address || f.address,
        city: ex.data.city || f.city,
        postal_code: ex.data.postal_code || f.postal_code,
      }));
      // If KI extracted any address field, auto-uncheck "same household"
      if (ex.data.address || ex.data.city || ex.data.postal_code) {
        setSameHousehold(false);
      }
      toast.success("KI hat das Dokument gelesen — bitte prüfen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "KI-Extraktion fehlgeschlagen");
    } finally { setExtracting(false); }
  };

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("Vorname und Nachname sind erforderlich");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/clients", {
        ...form,
        client_type: "person",
        // Required by backend — sensible defaults for family members of an existing client
        quelle: "Bestandskunde gleicher Haushalt",
        erstkontakt_durch: "berater",
      });
      const newId = res.data?.id;
      if (!newId) throw new Error("Keine Client-ID");
      // Re-attach the ID document to the new client
      if (docId) {
        try {
          await api.patch(`/documents/${docId}/attach-client`, null, {
            params: { client_id: newId, doc_type: "carta_identita" },
          });
        } catch { /* non-blocking */ }
      }
      await onCreated(newId);
      toast.success(`${form.first_name} ${form.last_name} angelegt und verbunden`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handlePlzChange = async (v) => {
    setForm((f) => ({ ...f, postal_code: v }));
    if (/^\d{4}$/.test(v.trim())) {
      const res = await lookupSwissPLZ(v);
      if (res) setForm((f) => ({ ...f, postal_code: v, city: res.city || f.city, canton: res.canton || f.canton }));
    }
  };

  return (
    <div className="space-y-3" data-testid="family-ai-form">
      {!extracted ? (
        <div
          className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-prime/40 transition-colors cursor-pointer"
          onClick={() => fileRef.current?.click()}
          data-testid="family-ai-dropzone"
        >
          <input
            ref={fileRef} type="file" accept="image/*,application/pdf"
            className="hidden" onChange={onFile}
            data-testid="family-ai-file-input"
          />
          {extracting ? (
            <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin text-prime" />
              KI liest das Dokument…
            </div>
          ) : (
            <>
              <Sparkles className="w-8 h-8 text-prime mx-auto mb-2" strokeWidth={1.5} />
              <div className="text-sm font-medium">Ausweis / Pass / Permit hochladen</div>
              <div className="text-xs text-muted-foreground mt-1">
                Die KI extrahiert Name, Geburtsdatum, Nationalität, Adresse automatisch.
              </div>
              <div className="text-[10px] text-muted-foreground mt-2 uppercase tracking-wider">
                JPG · PNG · PDF
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="rounded-md bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 text-xs px-3 py-2 flex items-start gap-2">
            <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={2.5} />
            <span>KI-Extraktion abgeschlossen — bitte die Felder überprüfen und korrigieren falls nötig.</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Vorname *</Label>
              <Input value={form.first_name} onChange={(e) => update("first_name", e.target.value)} className="h-9" data-testid="ai-first-name" />
            </div>
            <div>
              <Label className="text-xs">Nachname *</Label>
              <Input value={form.last_name} onChange={(e) => update("last_name", e.target.value)} className="h-9" data-testid="ai-last-name" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Geburtsdatum</Label>
              <Input type="date" value={form.date_of_birth} onChange={(e) => update("date_of_birth", e.target.value)} className="h-9" data-testid="ai-dob" />
            </div>
            <div>
              <Label className="text-xs">Nationalität</Label>
              <Input value={form.nationality} onChange={(e) => update("nationality", e.target.value)} className="h-9" data-testid="ai-nationality" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Geburtsort</Label>
              <Input value={form.place_of_birth} onChange={(e) => update("place_of_birth", e.target.value)} className="h-9" />
            </div>
            <div>
              <Label className="text-xs">Bewilligung</Label>
              <Input value={form.permit_type} onChange={(e) => update("permit_type", e.target.value)} className="h-9" placeholder="B / C / L / …" />
            </div>
          </div>
          {hasOwnerAddress && (
            <label className="flex items-center gap-2 cursor-pointer select-none py-1" data-testid="ai-same-household">
              <input
                type="checkbox"
                checked={sameHousehold}
                onChange={toggleSameHousehold}
                className="w-4 h-4 accent-prime"
              />
              <span className="text-xs text-foreground">
                Gleiches Domizil wie Hauptkunde
                <span className="ml-1 text-muted-foreground">
                  ({[ownerClient.postal_code, ownerClient.city].filter(Boolean).join(" ")})
                </span>
              </span>
            </label>
          )}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">PLZ</Label>
              <Input value={form.postal_code} onChange={(e) => handlePlzChange(e.target.value)} disabled={sameHousehold && hasOwnerAddress} className="h-9 disabled:opacity-60" data-testid="ai-postal-code" />
            </div>
            <div>
              <Label className="text-xs">Ort</Label>
              <Input value={form.city} onChange={(e) => update("city", e.target.value)} disabled={sameHousehold && hasOwnerAddress} className="h-9 disabled:opacity-60" />
            </div>
            <div>
              <Label className="text-xs">Kanton</Label>
              <Input value={form.canton} onChange={(e) => update("canton", e.target.value)} disabled={sameHousehold && hasOwnerAddress} className="h-9 disabled:opacity-60" />
            </div>
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1">
              Adresse
              {ownerClient?.address && sameHousehold && (
                <span className="text-[10px] text-emerald-700 dark:text-emerald-400 font-normal">· aus Hauptkunde übernommen</span>
              )}
            </Label>
            <Input value={form.address} onChange={(e) => update("address", e.target.value)} disabled={sameHousehold && hasOwnerAddress} className="h-9 disabled:opacity-60" />
          </div>
          <Button onClick={submit} disabled={saving} className="w-full" data-testid="ai-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Anlegen & verbinden
          </Button>
        </>
      )}
    </div>
  );
}



const FAM_REL_SHORT = {
  coniuge: "Ehepartner",
  marito: "Ehemann", moglie: "Ehefrau",
  figlio: "Sohn", figlia: "Tochter",
  padre: "Vater", madre: "Mutter",
  fratello: "Bruder", sorella: "Schwester",
};

function FamilyCoverageOverview({ clientId }) {
  const navigate = useNavigate();
  const [members, setMembers] = useState(null); // null=loading, []=empty
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/clients/${clientId}/family/coverage`);
        if (alive) setMembers(r.data?.members || []);
      } catch {
        if (alive) { setError(true); setMembers([]); }
      }
    })();
    return () => { alive = false; };
  }, [clientId]);

  if (members === null) return null;
  if (error || members.length === 0) return null;

  const fmtDob = (s) => {
    if (!s) return "";
    // Convert YYYY-MM-DD to DD.MM.YYYY for display, else show as-is
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : s;
  };

  const levelStyle = {
    high_risk: {
      ring: "ring-prime/40 hover:ring-prime",
      bar: "bg-prime",
      pct: "text-prime",
      pill: "bg-prime/10 text-prime",
      label: "Rischio alto",
    },
    partial: {
      ring: "ring-amber-500/40 hover:ring-amber-500",
      bar: "bg-amber-500",
      pct: "text-amber-700 dark:text-amber-400",
      pill: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
      label: "Parziale",
    },
    covered: {
      ring: "ring-green-500/40 hover:ring-green-500",
      bar: "bg-green-600",
      pct: "text-green-700 dark:text-green-400",
      pill: "bg-green-500/10 text-green-700 dark:text-green-400",
      label: "Coperto",
    },
  };

  return (
    <div className="mt-5 pt-4 border-t border-border" data-testid="family-coverage-overview">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users2 className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.7} />
          <span className="label-uppercase text-muted-foreground">Copertura del nucleo familiare</span>
          <span className="text-[10px] font-mono text-muted-foreground">{members.length}</span>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
        {members.map((m) => {
          const s = levelStyle[m.level] || levelStyle.high_risk;
          const initials = `${(m.first_name?.[0] || "").toUpperCase()}${(m.last_name?.[0] || "").toUpperCase()}`;
          return (
            <button
              key={m.client_id}
              type="button"
              onClick={() => navigate(`/clients/${m.client_id}`)}
              className={`group text-left bg-card border border-border rounded-lg p-3 ring-1 ring-inset transition-all ${s.ring} hover:-translate-y-0.5`}
              data-testid={`family-cov-${m.client_id}`}
              title="Kundenkarte öffnen"
            >
              <div className="flex items-start gap-2.5">
                <div className="w-9 h-9 rounded-full bg-foreground/85 text-background flex items-center justify-center text-xs font-bold shrink-0 overflow-hidden">
                  {m.photo_url ? (
                    <img src={m.photo_url} alt="" className="w-full h-full object-cover" />
                  ) : initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate group-hover:text-prime transition-colors">
                    {m.first_name} {m.last_name}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${s.pill}`}>
                      {FAM_REL_SHORT[m.relationship] || m.relationship || "—"}
                    </span>
                    {m.date_of_birth && (
                      <span className="text-[10px] text-muted-foreground font-mono">{fmtDob(m.date_of_birth)}</span>
                    )}
                  </div>
                </div>
                <div className={`text-right shrink-0 ${s.pct}`}>
                  <div className="font-display font-extrabold text-lg leading-none tabular-nums">{m.percent}%</div>
                  <div className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">
                    {m.covered}/{m.total}
                  </div>
                </div>
              </div>
              {/* Mini progress bar */}
              <div className="mt-2.5 h-1.5 bg-secondary rounded-full overflow-hidden">
                <div
                  className={`h-full ${s.bar} transition-all`}
                  style={{ width: `${Math.max(2, m.percent)}%` }}
                />
              </div>
              <div className="mt-1.5 flex items-center justify-between">
                <span className={`text-[10px] font-bold uppercase tracking-wider ${s.pct}`}>{s.label}</span>
                <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

